/**
 * Feb 2026 · Public Studio Booking Widget — /book/:wsId
 * 4-step flow: pick studio+space → pick slot → pick add-ons → checkout.
 * Fully theme-able via GET /public/studio/widget/:wsId → widget_config.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { useParams, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { toast } from "sonner";
import { Calendar as ShadCalendar } from "@/components/ui/calendar";
import { load as loadCashfree } from "@cashfreepayments/cashfree-js";

// Feb 2026 · Luxe design tokens — piped as an inline <style> so the
// widget stays fully self-contained (no external stylesheet needed).
// Also imports serif from Google Fonts on-demand.
const luxeInlineStyles = `
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;500;600&display=swap');
.lx-hero-inner { animation: lxFadeUp 0.7s ease both; }
@keyframes lxFadeUp { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: none; } }
.lx-tick { animation: lxPop 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) both; }
@keyframes lxPop { from { opacity: 0; transform: scale(0.6); } to { opacity: 1; transform: scale(1); } }
.lx-offer:hover { transform: translateY(-2px); box-shadow: 0 12px 28px -12px rgba(36,26,17,0.18); }
.lx-offer-countdown-urgent { animation: lxOfferPulse 1.6s ease-in-out infinite; }
@keyframes lxOfferPulse { 0%,100% { box-shadow: 0 0 0 0 rgba(200,48,46,0.25); } 50% { box-shadow: 0 0 0 6px rgba(200,48,46,0); } }
.lx-auto-promo-toast {
  background: linear-gradient(135deg, #FFF8E8 0%, #FFEEC8 100%) !important;
  border: 1px solid #D9A851 !important;
  border-left: 4px solid #B98B33 !important;
  color: #3A2A14 !important;
  box-shadow: 0 10px 30px -8px rgba(185,139,51,0.35) !important;
  font-family: Georgia, 'Cormorant Garamond', serif !important;
}
.lx-auto-promo-toast [data-description] {
  color: #5C4A38 !important;
  font-family: inherit !important;
  font-size: 11px !important;
  letter-spacing: 0.3px;
}
.lx-space-tile { transition: all 0.28s cubic-bezier(0.4, 0, 0.2, 1); }
.lx-space-tile:hover { transform: translateY(-4px); box-shadow: 0 20px 40px -16px rgba(36,26,17,0.22); }
.lx-space-tile:active { transform: translateY(-2px); }
.lx-btn-primary { transition: all 0.24s ease; letter-spacing: 2px; text-transform: uppercase; font-weight: 700; font-size: 12px; }
.lx-btn-primary:hover { transform: translateY(-1px); box-shadow: 0 12px 24px -8px rgba(36,26,17,0.28); }
.lx-input { transition: border-color 0.2s ease; scroll-margin-top: 88px; }
.lx-input:focus { outline: 0; border-color: #B8860B !important; box-shadow: 0 0 0 3px rgba(184,134,11,0.15); }
/* Every input under the card gets clearance below the sticky stepper
   when it's focused — kills the "input hides under the header" bug */
.lx-card input, .lx-card textarea, .lx-card select { scroll-margin-top: 88px; }
/* Sticky stepper — locks below the hero so customers always see progress + can jump back */
.lx-stepper-wrap { position: sticky; top: 0; z-index: 40;
  background: linear-gradient(180deg, #FFFFFF 0%, #FBF7F0 90%, rgba(251,247,240,0) 100%);
  padding: 12px 0 16px; margin: -12px -18px 20px; padding-left: 18px; padding-right: 18px;
  border-bottom: 1px solid #F3ECE0; }
.lx-step { flex: 1; padding: 10px 4px 8px; background: transparent; border: 0;
  border-bottom: 2px solid #F3ECE0; font-size: 11px; text-transform: uppercase; letter-spacing: 2px;
  color: #B0A292; font-weight: 700; cursor: default; text-align: center;
  transition: color 0.2s ease, border-color 0.2s ease; white-space: nowrap; }
.lx-step[data-reached="1"] { color: #241A11; border-bottom-color: #B8860B; }
.lx-step[data-active="1"] { color: #B8860B; border-bottom-color: #B8860B; }
.lx-step[data-past="1"] { cursor: pointer; }
.lx-hero-h1 { line-height: 1.1; padding-bottom: 6px; }
/* Feb 2026 · Tighter calendar. shadcn calendar is ~340px wide by default;
   we shrink cells and center it inside its column so it doesn't dwarf
   the time selectors next to it */
.lx-calendar-wrap { text-align: center; }
.lx-calendar-wrap > .lx-calendar-inner { display: inline-block; }
.lx-calendar-wrap .rdp-months { font-size: 13px; }
.lx-calendar-wrap .rdp-day, .lx-calendar-wrap .rdp-head_cell {
  width: 34px !important; height: 34px !important; font-size: 12px;
}
.lx-calendar-wrap .rdp-caption_label { font-size: 14px; font-weight: 600; }
.lx-calendar-wrap .rdp-nav_button { width: 26px; height: 26px; }
/* Feb 2026 · R280.5 · Today's cell shouldn't feel pre-selected. The
   shadcn .rdp-day_today default background is the workspace accent
   (solid orange in Story & Fame) which looked identical to the
   selected state. Give it a soft tinted ring instead so customers
   read it as "today" not "picked". Selection wins the moment they
   tap a date. */
.lx-calendar-wrap .rdp-day_today:not([aria-selected="true"]):not(.rdp-day_selected) {
  background: transparent !important;
  color: inherit !important;
  box-shadow: inset 0 0 0 1.5px rgba(184, 134, 11, 0.35);
  font-weight: 600;
}
.lx-calendar-wrap .rdp-day_today[aria-selected="true"],
.lx-calendar-wrap .rdp-day_today.rdp-day_selected {
  box-shadow: none;
}
/* Continue button row — centered, generous top margin, consistent
   spacing across all steps */
/* Feb 2026 · R268lll · Invalid-field flash — red outline + shake to
   draw the customer's eye to what's missing on Book-Now. */
.lx-invalid { animation: lxShake 320ms ease-in-out; box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.25); }
@keyframes lxShake {
  0%, 100% { transform: translateX(0); }
  20% { transform: translateX(-4px); }
  40% { transform: translateX(4px); }
  60% { transform: translateX(-3px); }
  80% { transform: translateX(3px); }
}
/* Feb 2026 · R268kkk · Step 2 two-column layout collapses to a
   single column on tablets & phones so the calendar stays readable. */
.lx-step2-grid { display: grid; }
@media (max-width: 767px) {
  .lx-step2-grid { grid-template-columns: 1fr !important; gap: 20px !important; }
}
.lx-continue-row { display: flex; justify-content: center; margin-top: 24px; }
/* Sticky checkout bar on mobile so the primary CTA is always reachable */
@media (max-width: 640px) {
  .lx-hero-h1 { font-size: 34px !important; }
  .lx-step { font-size: 10px; letter-spacing: 1.2px; padding: 8px 2px 6px; }
  .lx-card { padding: 18px 14px 24px !important; }
  .lx-container { padding: 12px !important; padding-bottom: 24px !important; }
  /* R280.5 · Extra top padding so the "🏠 Home" pill (top:20)
     doesn't overlap the hero eyebrow / title on narrow phones. */
  .lx-hero { padding: 78px 16px 60px !important; }
  /* Feb 2026 · When the sticky pay bar is actually rendered (compact
     single-page mode on an active quote), restore the extra bottom
     padding so the CTA never covers content. The parent toggles
     the lx-has-sticky-pay class on the widget-root when the bar mounts. */
  .lx-has-sticky-pay .lx-container { padding-bottom: 96px !important; }
  .lx-has-sticky-pay .lx-card { padding-bottom: 96px !important; }
  .lx-hero-home {
    top: 12px !important; left: 12px !important;
    padding: 6px 10px !important; font-size: 13px !important;
  }
  .lx-hero-home > span { display: none !important; }
  .lx-space-tile { padding: 14px !important; }
  .lx-space-serif { font-size: 18px !important; }
  .lx-offer { min-width: 200px !important; padding: 12px !important; }
  .lx-calendar-wrap .rdp-day, .lx-calendar-wrap .rdp-head_cell {
    width: 32px !important; height: 32px !important;
  }
  /* Mobile focus scroll — pushes input a bit further so the promo /
     name / email row sits comfortably below the sticky header */
  .lx-card input, .lx-card textarea, .lx-card select { scroll-margin-top: 96px; }
}
/* Feb 2026 · R268yyy · Sticky pay bar tuning. Keeps the total label
   and the primary CTA on ONE ROW even at 320-400px viewports (iPhone
   SE / small Androids) by tightening padding, shrinking fonts, and
   allowing the button to shrink instead of wrap. */
.lx-sticky-pay { transition: padding 200ms ease; }
.lx-sticky-pay-inner { display: flex; align-items: center; gap: 14px; flex-wrap: nowrap; max-width: 760px; margin: 0 auto; }
.lx-sticky-pay-info { display: flex; flex-direction: column; flex: 1 1 auto; min-width: 0; overflow: hidden; }
.lx-sticky-pay-info > * { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.lx-sticky-pay-btn { flex: 0 0 auto; min-width: 200px; }
@media (max-width: 480px) {
  .lx-sticky-pay { padding: 10px 12px !important; }
  .lx-sticky-pay-inner { gap: 10px; }
  .lx-sticky-pay-btn { min-width: 0 !important; padding: 10px 14px !important; font-size: 11px !important; letter-spacing: 1px !important; }
  .lx-sticky-pay-info .lx-sticky-total-label { font-size: 8px !important; letter-spacing: 1.5px !important; }
  .lx-sticky-pay-info .lx-sticky-deposit { font-size: 11px !important; }
}
@media (max-width: 360px) {
  .lx-sticky-pay-btn { padding: 10px 12px !important; font-size: 10px !important; }
}
/* Feb 2026 · Pulse animation — fires whenever the running total in
   the sticky bar changes so the customer catches the price update
   even when their eyes are on the extras/crew section further up. */
@keyframes lx-pulse-total {
  0%   { transform: scale(1);   color: currentColor; }
  40%  { transform: scale(1.06); color: #B8860B; }
  100% { transform: scale(1);   color: currentColor; }
}
.lx-sticky-pay-info.lx-pulse .lx-sticky-total-label,
.lx-sticky-pay-info.lx-pulse .lx-sticky-deposit {
  animation: lx-pulse-total 520ms ease-out;
  transform-origin: left center;
}
/* Feb 2026 · R268yyy · Compact-mode density overrides. Trims paddings,
   image sizes, calendar footprint and section spacing so the whole
   single-page booking form fits in far less scroll — targeted at users
   who chose compact precisely to avoid navigating pages. Overrides
   apply only when the widget-root carries .lx-compact. */
.lx-compact .lx-card { padding: 12px 14px 96px !important; }
.lx-compact .lx-container { margin-top: 0 !important; }
/* Studio + space cards */
.lx-compact [data-testid^="widget-studio-"],
.lx-compact [data-testid^="widget-space-"] { padding: 10px 12px !important; }
.lx-compact [data-testid^="widget-studio-"] img,
.lx-compact [data-testid^="widget-space-"] img { max-height: 44px !important; max-width: 44px !important; }
/* Section header strips ("Section N of 4") */
.lx-compact [data-testid^="widget-step-"] > div:first-child { margin: 10px 0 8px !important; padding: 7px 10px !important; font-size: 9px !important; letter-spacing: 2.5px !important; }
/* Section titles ("1. Choose your studio", etc.) */
.lx-compact h2, .lx-compact h3 { font-size: 15px !important; margin: 8px 0 6px !important; }
.lx-compact .lx-section-title { font-size: 9px !important; margin-bottom: 6px !important; letter-spacing: 2px !important; }
/* Calendar shrink */
.lx-compact .lx-calendar-inner { padding: 6px !important; }
.lx-compact .rdp,
.lx-compact .rdp-root { --rdp-cell-size: 30px !important; font-size: 12px !important; }
.lx-compact .rdp-caption_label,
.lx-compact .rdp-caption { font-size: 12px !important; padding: 2px 4px !important; }
.lx-compact .rdp-head_cell { font-size: 10px !important; height: 22px !important; }
.lx-compact .rdp-day, .lx-compact .rdp-button { height: 30px !important; width: 30px !important; font-size: 12px !important; }
.lx-compact .rdp-months { gap: 4px !important; }
/* Offer strip */
.lx-compact [data-testid="widget-offers"] { padding: 6px 0 !important; }
.lx-compact [data-testid^="widget-offer-"] { min-width: 150px !important; padding: 8px 10px !important; font-size: 11px !important; }
/* Extras / crew / services grids — tighter rows */
.lx-compact label { padding: 8px 10px !important; }
.lx-compact input, .lx-compact select, .lx-compact textarea { padding: 8px 10px !important; font-size: 13px !important; }
.lx-compact textarea { min-height: 60px !important; }
/* Reduce top-level margins between sections */
.lx-compact [data-testid^="widget-step-"] { margin-top: 4px !important; }
/* Slot summary + call-whatsapp bars unused in compact — but if any
   sneak through, tighten them */
.lx-compact [data-testid="widget-slot-summary"] { padding: 8px 10px !important; margin: 8px 0 !important; }
/* Selected studio meta card */
.lx-compact [data-testid="widget-current-studio"] { padding: 10px 12px !important; margin: 8px 0 !important; }
/* Time-select inputs */
.lx-compact select { padding: 6px 8px !important; height: 34px !important; }
/* Buttons full width but shorter */
.lx-compact button { min-height: unset; }
/* Powered by footer */
.lx-compact [data-testid="widget-powered"] { padding-top: 12px !important; font-size: 10px !important; }
/* Feb 2026 · R268yyy · Additional compact density trim — tighter
   inter-section gaps, smaller offers strip, more compact selected
   studio meta card and reduced grid gaps on the date/time row so the
   whole one-page form fits well within a single laptop viewport. */
.lx-compact [data-testid="widget-step-2"] > div { gap: 12px !important; }
.lx-compact [data-testid^="widget-step-"] { padding-top: 0 !important; }
.lx-compact [data-testid="widget-step-2"] .grid,
.lx-compact [data-testid="widget-step-3"] .grid { gap: 10px !important; }
/* Offers strip — 60% smaller */
.lx-compact [data-testid="widget-offers"] > * { transform: scale(0.85); transform-origin: left center; }
/* Slot summary card (when it does render) — flatter */
.lx-compact [data-testid="widget-slot-summary"] { margin: 6px 0 !important; padding: 6px 10px !important; font-size: 11px !important; }
/* Extras grid tiles — smaller checkbox rows */
.lx-compact [data-testid^="widget-eq-"],
.lx-compact [data-testid^="widget-equipment-"],
.lx-compact [data-testid^="widget-crew-"],
.lx-compact [data-testid^="widget-service-"] { padding: 6px 8px !important; font-size: 12px !important; }
/* Currency & rate hints under equipment rows */
.lx-compact [data-testid^="widget-eq-"] > *:last-child { font-size: 10px !important; }
/* Selected studio meta card image */
.lx-compact [data-testid="widget-current-studio"] img { max-height: 40px !important; max-width: 40px !important; }
/* Powered by row tighter */
.lx-compact [data-testid="widget-powered"] { margin-top: 8px !important; }
/* Reduce vertical whitespace after the compact header */
.lx-compact [data-testid="widget-compact-header"] { padding: 8px 20px !important; }
/* Studio + space card list — collapse row gap */
.lx-compact [data-testid^="widget-studio-"] + [data-testid^="widget-studio-"],
.lx-compact [data-testid^="widget-space-"] + [data-testid^="widget-space-"] { margin-top: 6px !important; }
`;


// Feb 2026 · Local-date formatter — `.toISOString().slice(0,10)` returns
// the **UTC** date, which shifts one day back for every user east of
// UTC (all of India, SEA, AU, JP …). That off-by-one bug made the
// calendar feel sluggish: users clicked Feb 20, the state stored
// Feb 19, the picked-modifier highlighted Feb 19, and users clicked
// again to correct — reading as "the picker didn't respond".
function toLocalYMD(d) {
  if (!d) return "";
  const yy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
}

// Feb 2026 · Human-friendly date. Kept locale-neutral (en-IN default)
// so the "Wed 21 Feb 2026" format reads the same for the studio team
// and the customer. Falls back to the raw YMD string if the input
// isn't parseable — never throws.
function formatFriendlyDate(ymd) {
  if (!ymd) return "";
  try {
    const d = new Date(`${ymd}T00:00:00`);
    return d.toLocaleDateString("en-IN", {
      weekday: "short", day: "2-digit", month: "short", year: "numeric",
    });
  } catch (_e) { return ymd; }
}

// Feb 2026 · R268jjj · Curated Google Fonts + system stack. The
// widget config stores just the slug ("playfair", "inter", …) — this
// map resolves the slug to a real font-family stack and the Google
// Fonts URL that `useGoogleFonts` injects on demand.
const FONT_STACKS = {
  system:      "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
  inter:       "'Inter', system-ui, sans-serif",
  playfair:    "'Playfair Display', 'Cormorant Garamond', Georgia, serif",
  cormorant:   "'Cormorant Garamond', 'EB Garamond', Georgia, serif",
  dmserif:     "'DM Serif Display', Georgia, serif",
  spacegrotesk: "'Space Grotesk', system-ui, sans-serif",
  lora:        "'Lora', Georgia, serif",
  manrope:     "'Manrope', system-ui, sans-serif",
  fraunces:    "'Fraunces', Georgia, serif",
  ibmplex:     "'IBM Plex Sans', system-ui, sans-serif",
  merriweather: "'Merriweather', Georgia, serif",
  poppins:     "'Poppins', system-ui, sans-serif",
};
const GOOGLE_FONT_PARAMS = {
  inter:        "Inter:wght@400;500;600;700",
  playfair:     "Playfair+Display:wght@400;500;600;700",
  cormorant:    "Cormorant+Garamond:wght@400;500;600;700",
  dmserif:      "DM+Serif+Display",
  spacegrotesk: "Space+Grotesk:wght@400;500;600;700",
  lora:         "Lora:wght@400;500;600;700",
  manrope:      "Manrope:wght@400;500;600;700",
  fraunces:     "Fraunces:wght@400;500;600;700",
  ibmplex:      "IBM+Plex+Sans:wght@400;500;600;700",
  merriweather: "Merriweather:wght@400;700",
  poppins:      "Poppins:wght@400;500;600;700",
};
// Feb 2026 · Injects the required Google Fonts <link> once per session.
// Safe to call multiple times — dedupes by data-slug attribute.
function useGoogleFonts(slugs) {
  useEffect(() => {
    if (typeof document === "undefined") return;
    slugs.filter(Boolean).forEach((slug) => {
      const q = GOOGLE_FONT_PARAMS[slug];
      if (!q) return;
      if (document.head.querySelector(`link[data-gf="${slug}"]`)) return;
      const l = document.createElement("link");
      l.rel = "stylesheet";
      l.href = `https://fonts.googleapis.com/css2?family=${q}&display=swap`;
      l.setAttribute("data-gf", slug);
      document.head.appendChild(l);
    });
  }, [slugs.join("|")]); // eslint-disable-line react-hooks/exhaustive-deps
}

// Feb 2026 · Live countdown label for the Special Offers strip.
// Returns `null` when the offer has no `ends_at`, has already
// expired, or ends more than 7 days away (chip hidden for
// far-future offers). `{ label, urgent }` otherwise — `urgent`
// flips true inside the last 24 hr so the chip pulses red to drive
// the booking decision.
function _offerCountdownLabel(endsAtIso, nowMs) {
  if (!endsAtIso) return null;
  const end = new Date(endsAtIso).getTime();
  if (!Number.isFinite(end)) return null;
  const diffMs = end - (nowMs || Date.now());
  if (diffMs <= 0) return null;
  const diffH = diffMs / 3.6e6;
  if (diffH > 7 * 24) return null;
  const d = Math.floor(diffH / 24);
  const h = Math.floor(diffH % 24);
  const m = Math.floor((diffMs / 60000) % 60);
  let label;
  if (d >= 1) label = `${d}d ${h}h`;
  else if (h >= 1) label = `${h}h ${m}m`;
  else label = `${m}m`;
  return { label, urgent: diffH < 24 };
}


// Feb 2026 · R268qqq · Module-level absolute-URL helper for public
// assets (studio logos, guideline files, etc.). Any path starting with
// `/api/` gets prefixed with the backend origin so the widget can be
// embedded on third-party sites and still resolve backend media.
function _absPub(u) {
  if (!u) return "";
  if (u.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL || ""}${u}`;
  return u;
}

// Feb 2026 · R268kkk · Auto-advancing studio photo slideshow. Renders
// on Step 1 above the space-picker once the customer has picked a
// studio. Dot indicators, prev/next arrows, swipe on mobile, 4-second
// auto-advance pausing on hover. Empty gallery → returns null so
// upstream layout collapses gracefully.
function StudioSlideshow({ urls, logoUrl, alt, gold, bark800, cream200 }) {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const touch = useRef({ x: 0 });
  const _abs = (u) => (u && u.startsWith("/api/")) ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u;
  useEffect(() => {
    if (paused || !urls || urls.length <= 1) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % urls.length), 4000);
    return () => clearInterval(t);
  }, [paused, urls?.length]);
  if (!urls || urls.length === 0) return null;
  const cur = urls[idx];
  const go = (dir) => setIdx((i) => (i + dir + urls.length) % urls.length);
  return (
    <div
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={(e) => { touch.current.x = e.touches[0].clientX; }}
      onTouchEnd={(e) => {
        const dx = (e.changedTouches[0].clientX - touch.current.x) || 0;
        if (Math.abs(dx) > 40) go(dx > 0 ? -1 : 1);
      }}
      style={{
        position: "relative", width: "100%", aspectRatio: "16 / 9",
        overflow: "hidden", marginBottom: 20, background: "#111",
        border: `1px solid ${cream200}`,
      }}
      data-testid="widget-slideshow">
      {urls.map((u, i) => (
        <img key={u + i} src={_abs(u)} alt={alt || ""}
              loading={i === 0 ? "eager" : "lazy"}
              decoding="async"
              style={{
                position: "absolute", inset: 0, width: "100%", height: "100%",
                objectFit: "cover", opacity: i === idx ? 1 : 0,
                transition: "opacity 900ms ease-in-out",
              }} />
      ))}
      {logoUrl && (
        <img src={_abs(logoUrl)} alt="" style={{
          position: "absolute", top: 14, left: 14, height: 44, maxWidth: 140,
          objectFit: "contain", background: "rgba(255,255,255,0.9)",
          padding: "4px 8px", boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
        }} />
      )}
      {urls.length > 1 && (
        <>
          <button type="button" onClick={() => go(-1)} aria-label="Previous"
                    style={{
                      position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)",
                      width: 32, height: 32, background: "rgba(0,0,0,0.55)", color: "white",
                      border: 0, cursor: "pointer", fontSize: 18,
                    }} data-testid="widget-slideshow-prev">‹</button>
          <button type="button" onClick={() => go(1)} aria-label="Next"
                    style={{
                      position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
                      width: 32, height: 32, background: "rgba(0,0,0,0.55)", color: "white",
                      border: 0, cursor: "pointer", fontSize: 18,
                    }} data-testid="widget-slideshow-next">›</button>
          <div style={{
            position: "absolute", bottom: 12, left: "50%", transform: "translateX(-50%)",
            display: "flex", gap: 6,
          }}>
            {urls.map((_, i) => (
              <button key={i} onClick={() => setIdx(i)} aria-label={`Slide ${i + 1}`}
                       style={{
                         width: 8, height: 8, borderRadius: 999, border: 0, cursor: "pointer",
                         background: i === idx ? gold : "rgba(255,255,255,0.5)",
                         transition: "background 200ms",
                       }} data-testid={`widget-slideshow-dot-${i}`} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// R268vvv · Feb 2026 · Booking status timeline. Renders a horizontal
// progress rail showing where the booking is in its lifecycle
// (Received → Verifying → Confirmed → Reminder → Done). Read from
// `booking.status` + `booking.reminders_sent` + `booking.slot_end`.
// Compact enough to sit inside the confirmation card without pushing
// the fold; degrades gracefully to a stacked list on narrow screens.
function BookingStatusTimeline({ booking, primary, bark800, bark600, cream100, cream200, gold }) {
  const status = booking?.status || "pending_payment";
  const mode = booking?.payment_mode || "cashfree";
  // Timeline shape depends on payment mode — offline flows never
  // show a "verifying" step, cashfree flows skip "verifying" too.
  const steps = (() => {
    const base = [
      { key: "received", label: "Received" },
      ...(mode === "upi_qr" ? [{ key: "verifying", label: "Verifying" }] : []),
      { key: "confirmed", label: "Confirmed" },
      { key: "reminder",  label: "Reminder sent" },
      { key: "complete",  label: "Shoot complete" },
    ];
    return base;
  })();
  const now = new Date();
  const slotEnd = booking?.slot_end ? new Date(booking.slot_end) : null;
  const reminderSent = !!(booking?.reminders_sent?.day_before || booking?.reminders_sent?.two_hours);
  const activeKey = (() => {
    if (status === "cancelled") return null;
    if (status === "pending_payment") return "received";
    if (status === "pending_verification") return "verifying";
    if (slotEnd && now > slotEnd) return "complete";
    if (reminderSent) return "reminder";
    if (status === "confirmed") return "confirmed";
    return "received";
  })();
  const activeIdx = steps.findIndex((s) => s.key === activeKey);
  const passedIdx = activeIdx === -1 ? -1 : activeIdx;
  return (
    <div style={{ background: "white", border: `1px solid ${cream200}`, padding: "18px 16px",
                    marginTop: 20 }} data-testid="widget-done-timeline">
      <div style={{ fontSize: 10, letterSpacing: 3, textTransform: "uppercase",
                     color: bark600, fontWeight: 700, marginBottom: 12, textAlign: "center" }}>
        Booking status
      </div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                     position: "relative" }}>
        {/* rail */}
        <div style={{ position: "absolute", top: 12, left: 12, right: 12,
                        height: 2, background: cream200 }} />
        <div style={{ position: "absolute", top: 12, left: 12,
                        width: passedIdx <= 0 ? 0 : `calc(${(passedIdx / (steps.length - 1)) * 100}% - 24px * ${passedIdx / (steps.length - 1)})`,
                        height: 2, background: primary,
                        transition: "width 400ms ease" }} />
        {steps.map((s, i) => {
          const passed = i <= passedIdx;
          const current = i === passedIdx;
          return (
            <div key={s.key} style={{ display: "flex", flexDirection: "column",
                        alignItems: "center", position: "relative", zIndex: 1, minWidth: 0, flex: 1 }}
                  data-testid={`widget-done-timeline-step-${s.key}`}
                  data-active={current ? "1" : "0"}
                  data-passed={passed ? "1" : "0"}>
              <div style={{
                width: 24, height: 24, borderRadius: "50%",
                background: passed ? primary : "white",
                border: `2px solid ${passed ? primary : cream200}`,
                color: passed ? "white" : bark600,
                fontSize: 11, fontWeight: 700,
                display: "flex", alignItems: "center", justifyContent: "center",
                boxShadow: current ? `0 0 0 4px ${primary}22` : "none",
                transition: "all 220ms",
              }}>
                {passed ? (i < passedIdx ? "✓" : (i + 1)) : (i + 1)}
              </div>
              <div style={{ fontSize: 10, marginTop: 6, color: passed ? bark800 : bark600,
                              fontWeight: current ? 700 : 500, textAlign: "center",
                              maxWidth: 80, lineHeight: 1.2 }}>
                {s.label}
              </div>
            </div>
          );
        })}
      </div>
      {status === "cancelled" && (
        <div style={{ marginTop: 10, fontSize: 11, color: "#B91C1C", textAlign: "center", fontWeight: 600 }}>
          Booking cancelled
        </div>
      )}
    </div>
  );
}


// R268uuu · Feb 2026 · UPI-QR direct-collection panel. Renders the
// studio's UPI QR + payee name + txn-id + screenshot upload form.
// After submit, the booking transitions to "pending_verification"
// and the admin manually confirms in the app. Reusable across the
// steps + compact layouts because it's driven by `done.payment`.
function UpiPaymentPanel({ done, wsId, primary, bark800, bark600,
                             cream100, cream200, gold, serifFamily, font, btnBase }) {
  const pay = done?.payment || {};
  const b = done?.booking || {};
  const refId = b.ref_no || b.code;
  const [txnId, setTxnId] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(!!b.upi_proof);
  const submit = async () => {
    if (!txnId && !file) {
      toast.error("Enter your UPI transaction ID or attach the payment screenshot");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      if (txnId) fd.append("txn_id", txnId);
      if (file) fd.append("screenshot", file);
      if (b.customer_email) fd.append("customer_email", b.customer_email);
      await api.post(
        `/public/studio/bookings/${wsId}/${refId}/upi-proof`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setSubmitted(true);
      toast.success("Payment proof submitted — you'll get a confirmation email once verified.");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Couldn't submit — try again"));
    } finally { setBusy(false); }
  };
  if (submitted) {
    return (
      <div style={{ background: cream100, border: `1px solid ${gold}`, padding: 20, textAlign: "center" }}
            data-testid="widget-done-upi-pending">
        <div style={{ fontSize: 11, letterSpacing: 2, textTransform: "uppercase",
                        color: bark600, fontWeight: 700, marginBottom: 6 }}>
          Pending verification
        </div>
        <div style={{ fontFamily: serifFamily, fontSize: 20, color: bark800 }}>
          Proof received — we're checking with the bank
        </div>
        <div style={{ fontSize: 12, color: bark600, marginTop: 8 }}>
          You'll get a confirmation email once the studio verifies the payment (usually within a few hours).
        </div>
      </div>
    );
  }
  return (
    <div style={{ background: "white", border: `1px solid ${cream200}`, padding: 20, marginTop: 8 }}
          data-testid="widget-done-upi-panel">
      <div style={{ fontSize: 11, letterSpacing: 2, textTransform: "uppercase",
                     color: bark600, fontWeight: 700, textAlign: "center", marginBottom: 10 }}>
        Step 1 — Pay via UPI
      </div>
      {pay.qr_url && (
        <div style={{ textAlign: "center" }}>
          <img src={pay.qr_url} alt="UPI QR"
                data-testid="widget-done-upi-qr"
                style={{ maxWidth: 220, width: "100%", margin: "0 auto",
                          border: `1px solid ${cream200}`, padding: 8, background: "white" }} />
        </div>
      )}
      {pay.upi_id && (
        <div style={{ textAlign: "center", marginTop: 10, fontSize: 13, color: bark800 }}>
          <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 2, color: bark600 }}>
            UPI ID
          </div>
          <div style={{ fontFamily: "'Courier New', monospace", fontWeight: 700, marginTop: 2 }}>
            {pay.upi_id}
          </div>
          {pay.payee_name && (
            <div style={{ fontSize: 11, color: bark600, marginTop: 2 }}>{pay.payee_name}</div>
          )}
        </div>
      )}
      <div style={{ textAlign: "center", margin: "12px 0 4px", fontSize: 12, color: bark600 }}>
        Amount payable: <b style={{ color: bark800 }}>₹{(pay.amount || 0).toFixed(2)}</b>
      </div>
      {/* R268vvv · Feb 2026 · Native UPI deep-link. On mobile this
          opens the customer's UPI app (GPay/PhonePe/Paytm) with the
          amount + note pre-filled — one tap to pay. Hidden on
          desktop where UPI apps aren't installed. */}
      {pay.upi_deep_link && (
        <a href={pay.upi_deep_link}
            data-testid="widget-done-upi-deeplink"
            style={{
              display: "block", marginTop: 12,
              background: primary, color: "white",
              padding: "12px 20px", textAlign: "center",
              fontSize: 12, letterSpacing: 2, textTransform: "uppercase",
              fontWeight: 700, textDecoration: "none",
              border: `1px solid ${primary}`,
            }}>
          📱 Pay ₹{(pay.amount || 0).toFixed(2)} in UPI app
        </a>
      )}
      <div style={{ borderTop: `1px dashed ${cream200}`, margin: "16px -4px 12px" }} />
      <div style={{ fontSize: 11, letterSpacing: 2, textTransform: "uppercase",
                     color: bark600, fontWeight: 700, textAlign: "center", marginBottom: 10 }}>
        Step 2 — Confirm your payment
      </div>
      <input type="text" value={txnId} onChange={(e) => setTxnId(e.target.value)}
              placeholder="UPI transaction / reference ID"
              data-testid="widget-done-upi-txn"
              style={{ width: "100%", padding: 10, border: `1px solid ${cream200}`,
                        background: cream100, fontFamily: font, fontSize: 13,
                        color: bark800, boxSizing: "border-box", marginBottom: 8 }} />
      <label style={{ display: "block", fontSize: 12, color: bark600, marginBottom: 4 }}>
        Or attach payment screenshot (JPG/PNG, up to 5 MB)
      </label>
      <input type="file" accept="image/*"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="widget-done-upi-screenshot"
              style={{ width: "100%", fontSize: 12, color: bark800, marginBottom: 12 }} />
      <button type="button" onClick={submit} disabled={busy}
        data-testid="widget-done-upi-submit"
        style={{ ...btnBase, background: primary, color: "white",
                  border: `1px solid ${primary}`, width: "100%",
                  padding: "12px 20px", opacity: busy ? 0.6 : 1 }}>
        {busy ? "Submitting…" : "Submit payment proof →"}
      </button>
      <div style={{ fontSize: 11, color: bark600, marginTop: 8, textAlign: "center" }}>
        Booking stays pending until the studio verifies your payment.
      </div>
    </div>
  );
}


// R289 · Oct 2026 · Pre-book walk-in UPI payment panel. Shown on
// step 4 of the booking widget directly beneath the Pay CTA whenever
// the selected studio has a UPI QR / ID configured. Collapsed by
// default — a single tap reveals the QR + UPI ID + amount so a
// walk-in customer can pay the deposit on the spot. The actual
// booking submission still goes through the Pay button and marks the
// payment as pending_verification; the admin confirms once the money
// lands.
function PreBookWalkinPanel({
  studio, amount, cream100, cream200, gold, bark600, bark800,
  primary, serifFamily, font,
}) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  // R289.1 · Oct 2026 · Generate a short walk-in reference once per
  // mount so every scanned/opened QR carries the same traceable note.
  // Format: W-{YYMMDD}-{4 hex} — short enough to type into the UPI
  // app's note field by the customer, unique enough for the admin to
  // match against the inbound SMS/bank alert.
  const walkinRef = useMemo(() => {
    const d = new Date();
    const stamp = `${String(d.getFullYear()).slice(2)}${String(d.getMonth()+1).padStart(2,"0")}${String(d.getDate()).padStart(2,"0")}`;
    const hex = Math.floor(Math.random() * 0xffff).toString(16).padStart(4, "0").toUpperCase();
    return `W-${stamp}-${hex}`;
  }, []);
  const staticQr = studio?.upi_qr_url || "";
  const uid = studio?.upi_id || "";
  const payee = studio?.upi_payee_name || studio?.name || "";
  if (!staticQr && !uid) return null;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(uid);
      setCopied(true); toast.success("UPI ID copied");
      setTimeout(() => setCopied(false), 1800);
    } catch { /* silent */ }
  };
  // UPI deep-link with amount + payee + note (walk-in ref) baked in.
  // Fallback: static image QR when no UPI id is configured.
  const upiLink = uid
    ? `upi://pay?pa=${encodeURIComponent(uid)}${payee ? `&pn=${encodeURIComponent(payee)}` : ""}&am=${amount.toFixed(2)}&cu=INR&tn=${encodeURIComponent(walkinRef)}`
    : "";
  const copyBtn = {
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    padding: "4px 8px", fontSize: 10, letterSpacing: 1,
    textTransform: "uppercase", fontWeight: 700, cursor: "pointer",
    background: cream100, color: primary, border: `1px solid ${primary}`,
  };
  const copyRef = async () => {
    try {
      await navigator.clipboard.writeText(walkinRef);
      toast.success("Reference copied");
    } catch { /* silent */ }
  };
  return (
    <div data-testid="widget-walkin-panel"
          style={{
            width: "100%", maxWidth: 480, marginTop: 10,
            background: cream100, border: `1px solid ${cream200}`,
            borderLeft: `3px solid ${gold}`,
          }}>
      <button type="button" onClick={() => setOpen(!open)}
              data-testid="widget-walkin-toggle"
              style={{
                width: "100%", padding: "10px 14px", border: 0, background: "transparent",
                display: "flex", alignItems: "center", justifyContent: "space-between",
                cursor: "pointer", fontFamily: font, textAlign: "left",
              }}>
        <div>
          <div style={{ fontSize: 10, letterSpacing: 2, textTransform: "uppercase",
                          color: gold, fontWeight: 700, lineHeight: 1 }}>
            Walk-in?
          </div>
          <div style={{ fontSize: 13, color: bark800, marginTop: 4, fontWeight: 600 }}>
            Scan QR to pay the deposit now
          </div>
        </div>
        <div style={{ fontSize: 18, color: bark600, lineHeight: 1 }}>{open ? "−" : "+"}</div>
      </button>
      {open && (
        <div style={{ padding: "0 14px 14px", borderTop: `1px dashed ${cream200}` }}
              data-testid="widget-walkin-panel-body">
          <div style={{ fontSize: 11, color: bark600, lineHeight: 1.5, margin: "10px 0" }}>
            Already at the studio? Scan the QR below with any UPI app, pay the deposit, then come back here and tap <b>{"\u201C"}Book now{"\u201D"}</b> above — the studio will match your payment to the booking.
          </div>
          {/* R289.1 · Prefer the dynamic UPI deep-link QR (amount +
              payee + reference baked in). The static studio QR is a
              fallback for workspaces that haven't entered a UPI id. */}
          {upiLink ? (
            <div style={{ textAlign: "center" }}>
              <div style={{
                display: "inline-block", padding: 10, background: "white",
                border: `1px solid ${cream200}`,
              }}>
                <QRCodeSVG value={upiLink} size={190} level="M" includeMargin={false}
                            data-testid="widget-walkin-qr" />
              </div>
              <div style={{ fontSize: 10, color: bark600, marginTop: 6 }}>
                Scanning auto-fills <b style={{ color: bark800 }}>₹{amount.toFixed(2)}</b> and the reference below
              </div>
            </div>
          ) : staticQr ? (
            <div style={{ textAlign: "center" }}>
              <img src={staticQr} alt="Studio UPI QR"
                    data-testid="widget-walkin-qr"
                    style={{ maxWidth: 200, width: "100%", margin: "0 auto",
                              border: `1px solid ${cream200}`, padding: 6, background: "white" }} />
            </div>
          ) : null}
          {/* R289.1 · Walk-in reference. Tells the customer to put this
              in the UPI note field so the admin can match the inbound
              txn to the correct booking. When the dynamic QR is used
              it's already pre-filled, but we still display it so the
              customer can read it aloud / forward it. */}
          <div style={{ textAlign: "center", marginTop: 12 }}>
            <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 2, color: gold, fontWeight: 700 }}>
              Walk-in reference
            </div>
            <div style={{
              display: "inline-flex", alignItems: "center", gap: 8,
              marginTop: 4, padding: "6px 10px", background: "white",
              border: `1px solid ${cream200}`,
            }}>
              <span style={{ fontFamily: "'Courier New', monospace", fontWeight: 700, color: bark800, fontSize: 13 }}
                    data-testid="widget-walkin-ref">{walkinRef}</span>
              <button type="button" onClick={copyRef}
                      data-testid="widget-walkin-ref-copy"
                      style={copyBtn}>
                Copy
              </button>
            </div>
            <div style={{ fontSize: 10, color: bark600, marginTop: 4 }}>
              Share this with the studio if asked.
            </div>
          </div>
          {uid && (
            <div style={{ textAlign: "center", marginTop: 10 }}>
              <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 2, color: bark600 }}>
                UPI ID
              </div>
              <div style={{
                display: "inline-flex", alignItems: "center", gap: 8,
                marginTop: 4, padding: "6px 10px", background: "white",
                border: `1px solid ${cream200}`,
              }}>
                <span style={{ fontFamily: "'Courier New', monospace", fontWeight: 700, color: bark800, fontSize: 13 }}
                      data-testid="widget-walkin-upi-id">{uid}</span>
                <button type="button" onClick={copy}
                        data-testid="widget-walkin-upi-copy"
                        style={copyBtn}>
                  {copied ? "✓ Copied" : "Copy"}
                </button>
              </div>
              {payee && (
                <div style={{ fontSize: 11, color: bark600, marginTop: 4 }}>{payee}</div>
              )}
            </div>
          )}
          <div style={{ textAlign: "center", margin: "14px 0 4px", fontSize: 12, color: bark600 }}>
            Amount: <b style={{ color: bark800, fontVariantNumeric: "tabular-nums" }}>₹{amount.toFixed(2)}</b>
          </div>
          {upiLink && (
            <a href={upiLink}
                data-testid="widget-walkin-upi-deeplink"
                style={{
                  display: "block", marginTop: 10, background: primary, color: "white",
                  padding: "12px 20px", textAlign: "center", fontSize: 12,
                  letterSpacing: 2, textTransform: "uppercase", fontWeight: 700,
                  textDecoration: "none", border: `1px solid ${primary}`,
                }}>
              📱 Open UPI app
            </a>
          )}
          <div style={{ fontSize: 10, color: bark600, marginTop: 10, textAlign: "center", lineHeight: 1.5 }}>
            Paid? Tap <b>Book now</b> above to lock your slot. We&rsquo;ll confirm your booking once the deposit shows up in our account.
          </div>
        </div>
      )}
    </div>
  );
}


// Feb 2026 · R288 · Share tracking link block for the booking
// confirmation screen. Copy / Share (Web Share API) / WhatsApp buttons
// plus a reassuring line that the confirmation + tracking link have
// also landed in the customer's inbox. Falls back gracefully: if the
// browser has no `navigator.share`, the native share button is hidden.
function TrackingShareBlock({
  booking, refLabel, wsId, studioName, waUrl,
  cream100, cream200, gold, bark600, bark800, primary, btnBase,
}) {
  const refNo = booking?.ref_no || booking?.code;
  const email = (booking?.customer_email || "").trim();
  const trackingUrl = useMemo(() => {
    if (!refNo || !wsId) return "";
    const origin = (typeof window !== "undefined" && window.location?.origin) || "";
    const em = email ? `?email=${encodeURIComponent(email)}` : "";
    return `${origin}/track/${wsId}/${refNo}${em}`;
  }, [refNo, wsId, email]);
  const [copied, setCopied] = useState(false);
  const canNativeShare = typeof navigator !== "undefined" && !!navigator.share;
  if (!trackingUrl) return null;
  const shareText = `My booking ${refLabel}${studioName ? ` at ${studioName}` : ""} — track it here: ${trackingUrl}`;
  const waShare = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(trackingUrl);
      setCopied(true); toast.success("Tracking link copied");
      setTimeout(() => setCopied(false), 2200);
    } catch { toast.error("Could not copy — long-press the link below"); }
  };
  const nativeShare = async () => {
    try {
      await navigator.share({ title: `Booking ${refLabel}`, text: shareText, url: trackingUrl });
    } catch { /* user dismissed */ }
  };
  const btn = {
    ...btnBase, padding: "10px 14px", fontSize: 11, letterSpacing: 2,
    background: "white", color: bark800, border: `1px solid ${cream200}`,
    minWidth: 0,
  };
  return (
    <div style={{
      background: "white", border: `1px solid ${cream200}`,
      borderLeft: `3px solid ${gold}`, padding: 18, marginBottom: 20,
    }} data-testid="widget-done-share-block">
      <div style={{ fontSize: 10, letterSpacing: 2, textTransform: "uppercase", color: bark600, fontWeight: 700, marginBottom: 8 }}>
        Your booking tracking link
      </div>
      <div style={{
        display: "flex", alignItems: "center", gap: 8, padding: "8px 10px",
        background: cream100, border: `1px solid ${cream200}`, fontSize: 11,
        wordBreak: "break-all", color: bark800, fontFamily: "'Courier New', monospace",
      }} data-testid="widget-done-share-url">
        {trackingUrl}
      </div>
      <div style={{
        display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
        gap: 8, marginTop: 10,
      }}>
        <button type="button" onClick={copyLink} style={{
          ...btn, color: primary, border: `1px solid ${primary}`,
        }} data-testid="widget-done-share-copy">
          {copied ? "✓ Copied" : "📋 Copy link"}
        </button>
        {canNativeShare && (
          <button type="button" onClick={nativeShare} style={{
            ...btn, color: bark800, border: `1px solid ${bark800}`,
          }} data-testid="widget-done-share-native">
            ⇪ Share
          </button>
        )}
        <a href={waShare} target="_blank" rel="noopener noreferrer" style={{
          ...btn, background: "#25D366", color: "white", border: "1px solid #25D366", textDecoration: "none",
        }} data-testid="widget-done-share-whatsapp">
          💬 WhatsApp
        </a>
      </div>
      <div style={{ marginTop: 12, padding: "8px 10px", background: cream100, border: `1px solid ${cream200}`,
                     fontSize: 11, color: bark600, display: "flex", alignItems: "flex-start", gap: 6 }}
            data-testid="widget-done-share-mailed-note">
        <span style={{ color: primary, fontSize: 13, lineHeight: 1 }}>✉</span>
        <span>
          A copy of the booking confirmation and this tracking link have been <b>emailed</b>
          {email ? <> to <b style={{ color: bark800 }}>{email}</b></> : null}. Check your spam folder if it doesn&rsquo;t arrive in a few minutes.
        </span>
      </div>
    </div>
  );
}

// Feb 2026 · R268qqq · Booking confirmation screen with studio
// branding, next-action buttons, and a rating widget. Keeps the same
// warm-luxe palette as the booking flow; falls back gracefully when
// the studio has no logo / website configured.
function BookingDoneScreen({
  done, wsId, shareSlug, date, startTime, endTime,
  currentStudio, currentSpace, currentAmenities, isOffsite,
  wc, logoUrl, font, serifFamily,
  bark800, bark600, cream100, cream200, gold, primary,
  onNewBooking,
}) {
  const booking = done?.booking || {};
  const refNo = booking.ref_no;
  const refLabel = refNo ? `#${refNo}` : (booking.code || "—");
  const studioLogo = currentStudio?.logo_url ? _absPub(currentStudio.logo_url) : logoUrl;
  const studioName = currentStudio?.name || wc.title || "";
  const websiteUrl = currentStudio?.website || "";
  const guidelinesUrl = currentStudio?.guidelines_file_url ? _absPub(currentStudio.guidelines_file_url) : "";
  const guidelinesName = currentStudio?.guidelines_file_name || "Studio guidelines";
  const callNum = currentStudio?.mobile || currentStudio?.phone || "";
  const waNum   = currentStudio?.whatsapp || currentStudio?.mobile || currentStudio?.phone || "";
  // Oct 2026 · Directions deep-link for the Done screen too.
  const directionsUrl = (() => {
    const s = currentStudio || {};
    if (s.map_url) return s.map_url;
    if (s.latitude != null && s.longitude != null) return `https://www.google.com/maps/?q=${s.latitude},${s.longitude}`;
    const addr = [s.address, s.city, s.state, s.pincode, s.country].filter(Boolean).join(", ");
    return addr ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr)}` : "";
  })();
  const norm = (n) => (n || "").replace(/[^\d+]/g, "");
  const waUrl = waNum
    ? `https://wa.me/${norm(waNum).replace(/^\+/, "")}?text=${encodeURIComponent(`Hi, I just booked ${refLabel}.`)}`
    : "";
  const [ratingScore, setRatingScore] = useState(0);
  const [ratingHover, setRatingHover] = useState(0);
  const [ratingComment, setRatingComment] = useState("");
  const [ratingSent, setRatingSent] = useState(false);
  const [ratingBusy, setRatingBusy] = useState(false);
  const submitRating = async () => {
    if (ratingScore < 1) { toast.error("Pick a rating first"); return; }
    setRatingBusy(true);
    try {
      const refPath = refNo ? String(refNo) : booking.code;
      await api.post(`/public/studio/bookings/${wsId}/${refPath}/rating`, {
        rating: ratingScore, comment: ratingComment,
        customer_email: booking.customer_email || "",
      });
      setRatingSent(true);
      toast.success("Thanks for the feedback!");
    } catch (e) { toast.error(apiErrorMessage(e, "Could not send rating")); }
    finally { setRatingBusy(false); }
  };
  const btnBase = {
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    gap: 8, padding: "12px 18px", fontSize: 12, fontWeight: 700,
    letterSpacing: 2, textTransform: "uppercase", textDecoration: "none",
    border: `1px solid ${bark800}`, cursor: "pointer",
    transition: "all 160ms cubic-bezier(0.4,0,0.2,1)",
  };
  return (
    <div style={{ minHeight: "100vh", background: `linear-gradient(180deg, ${cream100} 0%, #FFFFFF 100%)` }}>
      <style>{luxeInlineStyles}</style>
      <div style={{ maxWidth: 640, margin: "0 auto", padding: "48px 18px 96px", fontFamily: font, color: bark800 }} data-testid="widget-done">
        {/* Studio branding header — logo + name so the customer never
            loses context on which studio they just booked. */}
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          {studioLogo && (
            <img src={studioLogo} alt="" data-testid="widget-done-logo"
                  style={{ maxHeight: 56, maxWidth: 200, margin: "0 auto 12px", display: "block", objectFit: "contain" }} />
          )}
          {studioName && (
            <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 4, color: bark600, fontWeight: 600 }}>
              {studioName}
            </div>
          )}
          <div className="lx-tick" style={{
            width: 56, height: 56, borderRadius: "50%",
            background: cream200, border: `1px solid ${gold}`,
            color: primary, fontSize: 28, margin: "24px auto 0",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
          }}>✓</div>
          <h1 style={{ fontFamily: serifFamily, fontSize: 40, margin: "12px 0 4px", fontWeight: 500, letterSpacing: -0.5 }}>
            Booking received
          </h1>
        </div>
        {/* Numeric reference card — big, monospace, easy to read aloud */}
        <div style={{ background: "white", border: `1px solid ${cream200}`, borderTop: `2px solid ${primary}`, padding: "24px", textAlign: "center", marginBottom: 20 }}>
          <div style={{ fontSize: 10, letterSpacing: 3, textTransform: "uppercase", color: bark600, fontWeight: 700 }}>Reference</div>
          <div data-testid="widget-done-ref"
                style={{ fontFamily: "'Courier New', monospace", fontSize: 44, fontWeight: 700, letterSpacing: 4, color: bark800, marginTop: 6, lineHeight: 1 }}>
            {refLabel}
          </div>
          <div style={{ fontSize: 11, color: bark600, marginTop: 8 }}>Quote this number when you call or write in.</div>
        </div>
        {/* Feb 2026 · R288 · Share tracking link block. Customer gets a
            Copy / Share / WhatsApp (if available) trio + an explicit
            note that the confirmation and tracking link have been
            emailed. Rendered only when we have a numeric ref + customer
            email so the URL is actually resolvable. */}
        <TrackingShareBlock
          booking={booking} refLabel={refLabel} wsId={wsId}
          studioName={studioName} waUrl={waUrl}
          cream100={cream100} cream200={cream200} gold={gold}
          bark600={bark600} bark800={bark800} primary={primary} btnBase={btnBase}
        />
        {/* R268vvv · Booking status timeline — where you are in the flow */}
        <BookingStatusTimeline booking={booking}
          primary={primary} bark800={bark800} bark600={bark600}
          cream100={cream100} cream200={cream200} gold={gold} />
        <SlotSummaryStrip
          date={date} startTime={startTime} endTime={endTime}
          spaceLabel={currentSpace?.name} studioLabel={currentStudio?.name}
          isOffsite={isOffsite} editable={false}
          amenities={currentAmenities}
          cream100={cream100} cream200={cream200} gold={gold}
          bark600={bark600} bark800={bark800} />
        <div style={{ padding: 20, background: "white", border: `1px solid ${cream200}`, marginBottom: 20, textAlign: "center" }}>
          <div style={{ fontSize: 10, letterSpacing: 2, textTransform: "uppercase", color: bark600 }}>Total</div>
          <div style={{ fontFamily: serifFamily, fontSize: 32, fontWeight: 500 }}>₹{booking.quote?.total?.toFixed(2)}</div>
        </div>
        {/* R268uuu · Feb 2026 · UPI QR panel — rendered instead of the
            "redirecting to payment" line when the studio collects via
            UPI QR. Customer scans + pays + submits proof. */}
        {done?.payment?.provider === "upi_qr" ? (
          <UpiPaymentPanel done={done} wsId={wsId}
              primary={primary} bark800={bark800} bark600={bark600}
              cream100={cream100} cream200={cream200} gold={gold}
              serifFamily={serifFamily} font={font} btnBase={btnBase} />
        ) : done?.payment?.configured ? (
          <p style={{ color: bark600, fontSize: 13, textAlign: "center" }}>Redirecting to secure payment — complete payment to lock this slot.</p>
        ) : (
          <p style={{ color: bark600, fontSize: 13, textAlign: "center" }}>Our team will reach out to arrange payment shortly.</p>
        )}
        {/* Guidelines download — only when the studio uploaded one */}
        {guidelinesUrl && (
          <a href={guidelinesUrl} target="_blank" rel="noopener noreferrer"
              data-testid="widget-done-guidelines"
              style={{
                ...btnBase, background: "white", color: bark800, width: "100%",
                marginTop: 20, border: `1px solid ${cream200}`,
              }}>
            📄 Download studio guidelines
          </a>
        )}
        {wc.thank_you_html && (
          <div style={{ marginTop: 20, color: bark600, fontSize: 13, textAlign: "center" }}
                dangerouslySetInnerHTML={{ __html: wc.thank_you_html }} />
        )}
        {/* Rating widget — 5 stars + optional comment */}
        {!ratingSent ? (
          <div style={{ background: "white", border: `1px solid ${cream200}`, padding: 20, marginTop: 24, textAlign: "center" }}
                data-testid="widget-done-rating">
            <div style={{ fontSize: 11, letterSpacing: 2, textTransform: "uppercase", color: bark600, fontWeight: 700, marginBottom: 12 }}>
              How was the booking experience?
            </div>
            <div style={{ display: "flex", justifyContent: "center", gap: 6, marginBottom: 14 }}>
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} type="button"
                  onMouseEnter={() => setRatingHover(n)}
                  onMouseLeave={() => setRatingHover(0)}
                  onClick={() => setRatingScore(n)}
                  data-testid={`widget-done-rating-star-${n}`}
                  style={{ background: "transparent", border: 0, cursor: "pointer",
                            fontSize: 32, color: (ratingHover || ratingScore) >= n ? gold : cream200,
                            transition: "color 120ms", padding: 0 }}>
                  ★
                </button>
              ))}
            </div>
            <textarea
              data-testid="widget-done-rating-comment"
              value={ratingComment}
              onChange={(e) => setRatingComment(e.target.value)}
              placeholder="Anything we could improve? (optional)"
              rows={2}
              style={{ width: "100%", padding: 10, border: `1px solid ${cream200}`,
                        background: cream100, fontFamily: font, fontSize: 13,
                        color: bark800, resize: "vertical", boxSizing: "border-box" }} />
            <button type="button" onClick={submitRating} disabled={ratingBusy || ratingScore < 1}
              data-testid="widget-done-rating-submit"
              style={{ ...btnBase, background: bark800, color: "white",
                        marginTop: 10, opacity: ratingScore < 1 ? 0.5 : 1 }}>
              {ratingBusy ? "Sending…" : "Send feedback"}
            </button>
          </div>
        ) : (
          <div style={{ background: cream100, border: `1px solid ${cream200}`, padding: 16, marginTop: 24, textAlign: "center", fontSize: 13, color: bark600 }}
                data-testid="widget-done-rating-done">
            ✓ Thanks for the feedback — noted!
          </div>
        )}
        {/* Feb 2026 · R268rrr · Action strip.
            "Make another booking" is the primary CTA — full-width on
            mobile, uses the studio's primary color for prominence, and
            sits above the secondary row (website · call · WhatsApp)
            which shows only when the studio has those fields set. */}
        <button type="button" onClick={onNewBooking}
          data-testid="widget-done-again"
          style={{
            ...btnBase,
            background: primary, color: "white",
            border: `1px solid ${primary}`,
            width: "100%", padding: "16px 20px",
            fontSize: 13, letterSpacing: 3, marginTop: 32,
            boxShadow: `0 6px 18px -8px ${primary}`,
          }}
          onMouseOver={(e) => { e.currentTarget.style.transform = "translateY(-1px)";
                                 e.currentTarget.style.boxShadow = `0 10px 22px -8px ${primary}`; }}
          onMouseOut={(e) => { e.currentTarget.style.transform = "translateY(0)";
                                 e.currentTarget.style.boxShadow = `0 6px 18px -8px ${primary}`; }}>
          <span style={{ fontSize: 16, marginRight: 4 }}>↻</span> Make another booking
        </button>
        {/* Secondary contact strip — only rendered if the studio has
            configured at least one channel. Grid collapses to a single
            column on mobile (min-width per cell keeps it neat). */}
        {(websiteUrl || callNum || waNum || directionsUrl) && (
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: 10, marginTop: 12,
          }}>
            {websiteUrl && (
              <a href={websiteUrl} target="_blank" rel="noopener noreferrer"
                data-testid="widget-done-website"
                style={{ ...btnBase, background: "white", color: bark800 }}>
                🌐 Visit studio website
              </a>
            )}
            {callNum && (
              <a href={`tel:${norm(callNum)}`} data-testid="widget-done-call"
                style={{ ...btnBase, background: "white", color: primary, border: `1px solid ${primary}` }}>
                ☎ Call {callNum}
              </a>
            )}
            {directionsUrl && (
              <a href={directionsUrl} target="_blank" rel="noopener noreferrer"
                data-testid="widget-done-directions"
                style={{ ...btnBase, background: "white", color: primary, border: `1px solid ${primary}` }}>
                📍 Directions
              </a>
            )}
            {waNum && (
              <a href={waUrl} target="_blank" rel="noopener noreferrer"
                data-testid="widget-done-whatsapp"
                style={{ ...btnBase, background: "#25D366", color: "white", border: "1px solid #25D366" }}>
                💬 WhatsApp
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Feb 2026 · R268kkk · Persistent Call + WhatsApp CTA bar. Rendered
// on every step (1-4) once a studio has been picked so customers can
// pause and reach out at any point without losing their in-progress
// booking. Uses `mobile` for the call button, `whatsapp` for the WA
// button — both fall back to the legacy `phone` when unset.
function CallWhatsAppBar({ studio, primary, cream100, cream200 }) {
  if (!studio) return null;
  const call = studio.mobile || studio.phone || "";
  const wa   = studio.whatsapp || studio.mobile || studio.phone || "";
  // Oct 2026 · Directions deep-link. Show as "Directions" button
  // next to Call whenever the studio has either an explicit map_url,
  // lat/lng coords, or a usable postal address. Opens Google Maps /
  // Apple Maps / the default maps handler — the generic
  // `?q={address}` form works for every platform (Google Maps web
  // app on desktop, native Maps on iOS, Google Maps on Android).
  const mapsUrl = (() => {
    if (studio.map_url) return studio.map_url;
    if (studio.latitude != null && studio.longitude != null) {
      return `https://www.google.com/maps/?q=${studio.latitude},${studio.longitude}`;
    }
    const addr = [studio.address, studio.city, studio.state, studio.pincode, studio.country]
      .filter(Boolean).join(", ");
    if (addr) return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr)}`;
    return "";
  })();
  if (!call && !wa && !mapsUrl) return null;
  const norm = (n) => (n || "").replace(/[^\d+]/g, "");
  const waUrl = wa
    ? `https://wa.me/${norm(wa).replace(/^\+/, "")}?text=${encodeURIComponent(`Hi, I'm booking ${studio.name}.`)}`
    : "";
  return (
    <div data-testid="widget-ctabar" style={{
      display: "flex", gap: 8, padding: "10px 12px", background: cream100,
      border: `1px solid ${cream200}`, marginBottom: 16,
      alignItems: "center", flexWrap: "wrap",
    }}>
      <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 2, color: "#5C4A38", fontWeight: 700, marginRight: "auto" }}>
        Need help booking?
      </div>
      {call && (
        <a href={`tel:${norm(call)}`} data-testid="widget-cta-call"
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              padding: "8px 14px", background: "white",
              border: `1px solid ${primary}`, color: primary,
              textDecoration: "none", fontSize: 12, fontWeight: 700,
              letterSpacing: 1, textTransform: "uppercase",
            }}>
          ☎ Call {call}
        </a>
      )}
      {mapsUrl && (
        <a href={mapsUrl} target="_blank" rel="noopener noreferrer"
            data-testid="widget-cta-directions"
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              padding: "8px 14px", background: "white",
              border: `1px solid ${primary}`, color: primary,
              textDecoration: "none", fontSize: 12, fontWeight: 700,
              letterSpacing: 1, textTransform: "uppercase",
            }}>
          📍 Directions
        </a>
      )}
      {wa && (
        <a href={waUrl} target="_blank" rel="noopener noreferrer" data-testid="widget-cta-whatsapp"
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              padding: "8px 14px", background: "#25D366", color: "white",
              border: "1px solid #25D366", textDecoration: "none",
              fontSize: 12, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase",
            }}>
          <span style={{ fontSize: 14 }}>💬</span> WhatsApp
        </a>
      )}
    </div>
  );
}

// Feb 2026 · Compact "You picked …" summary strip. Shown on step 2
// once a date is chosen (so the customer sees confirmation *before*
// tapping Continue), and re-shown on steps 3 + 4 as a locked-in
// header so they never lose sight of the slot they're paying for.
// The `onEdit` hook jumps back to step 2. When `amenities` are passed
// (studio+space inheritance already resolved), they're displayed as
// small pills below the date/time so customers can double-check
// what's included in the space right up until confirmation.
function SlotSummaryStrip({ date, startTime, endTime, spaceLabel, studioLabel, isOffsite, onEdit, editable = true, amenities = null, cream100, cream200, gold, bark600, bark800 }) {
  if (!date) return null;
  const dateStr = formatFriendlyDate(date);
  const timeStr = startTime && endTime ? `${startTime} – ${endTime}` : "";
  const location = isOffsite
    ? "Off-site shoot"
    : [studioLabel, spaceLabel].filter(Boolean).join(" · ");
  const showAmenities = Array.isArray(amenities) && amenities.length > 0;
  return (
    <div
      data-testid="widget-slot-summary"
      style={{
        display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12,
        padding: "10px 14px", marginBottom: 16,
        background: cream100, border: `1px solid ${cream200}`,
        borderLeft: `3px solid ${gold}`,
      }}
    >
      <div style={{ flex: "1 1 auto", minWidth: 0 }}>
        <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 2, color: bark600, fontWeight: 700 }}>
          Your slot
        </div>
        <div style={{ fontSize: 14, fontWeight: 600, color: bark800, marginTop: 2 }}>
          <span data-testid="widget-slot-summary-date">{dateStr}</span>
          {timeStr && (<>
            <span style={{ margin: "0 8px", color: bark600 }}>·</span>
            <span data-testid="widget-slot-summary-time">{timeStr}</span>
          </>)}
        </div>
        {location && (
          <div style={{ fontSize: 12, color: bark600, marginTop: 2 }}
                data-testid="widget-slot-summary-location">
            {location}
          </div>
        )}
        {showAmenities && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 8 }}
                data-testid="widget-slot-summary-amenities">
            <span style={{ fontSize: 9, textTransform: "uppercase", letterSpacing: 1.5, color: bark600, fontWeight: 700, alignSelf: "center", marginRight: 4 }}>
              Included:
            </span>
            {amenities.slice(0, 8).map((a, i) => (
              <span key={a?.id || i} title={a?.description || ""}
                     style={{ fontSize: 10, background: "white", padding: "2px 8px",
                              color: bark800, border: `1px solid ${cream200}`, fontWeight: 600 }}>
                {a?.name || ""}
              </span>
            ))}
            {amenities.length > 8 && (
              <span style={{ fontSize: 10, color: bark600, alignSelf: "center" }}>
                +{amenities.length - 8} more
              </span>
            )}
          </div>
        )}
      </div>
      {editable && onEdit && (
        <button type="button" onClick={onEdit}
                data-testid="widget-slot-summary-edit"
                style={{
                  fontSize: 11, textTransform: "uppercase", letterSpacing: 2,
                  fontWeight: 700, color: bark800, background: "transparent",
                  border: `1px solid ${bark600}`, padding: "6px 12px",
                  cursor: "pointer",
                }}>
          Change
        </button>
      )}
    </div>
  );
}


export default function StudioBookingWidget() {
  const { wsId: urlParam } = useParams();
  const [params] = useSearchParams();
  const initialPromo = (params.get("promo") || "").toUpperCase();
  const shareSlug = params.get("sl") || null;
  // R268sss · Feb 2026 · Direct-link deep-link params. Any/all of the
  // below can be set on the URL to bypass earlier steps — the widget
  // auto-advances as soon as it has enough state to render the next
  // step. Great for paid-ad campaigns that want to drop customers
  // straight onto the date-picker or the pay screen.
  //   ?studio=<id>      → step 2 (date/time) pre-selected studio
  //   ?space=<id>       → step 2 pre-selected space (studio auto-derived)
  //   ?date=YYYY-MM-DD  → date pre-filled
  //   ?start=HH:MM      → start time pre-filled
  //   ?end=HH:MM        → end time pre-filled
  //   ?mode=compact     → force single-page compact layout, else use widget-config default
  //   ?entry=studio|offsite|<slug> → bypass the entry chooser
  const _urlStudio = params.get("studio") || null;
  const _urlSpace = params.get("space") || null;
  const _urlDate = params.get("date") || "";
  const _urlStart = params.get("start") || "";
  const _urlEnd = params.get("end") || "";
  const _urlMode = (params.get("mode") || "").toLowerCase();
  const _urlEntry = params.get("entry") || null;
  const [cfg, setCfg] = useState(null);
  // Feb 2026 · R268mmm · The route param may be a workspace UUID OR
  // a custom booking slug. We resolve once on load and pin the real
  // UUID into `wsId` so every downstream call talks IDs, not slugs.
  const isUuidLike = /^[0-9a-f]{8}-/i.test(urlParam || "");
  const [wsId, setWsId] = useState(isUuidLike ? urlParam : null);
  const [step, setStep] = useState(1);
  // Feb 2026 · Three-flow landing: null (chooser hidden or picked) |
  // "studio" | "offsite" | "other". When chooser is enabled the widget
  // opens with entryMode=null and shows a chooser tile grid.
  const [entryMode, setEntryMode] = useState(null);
  const [studioId, setStudioId] = useState(null);
  const [spaceId, setSpaceId] = useState(null);
  const [packageId, setPackageId] = useState(null);
  const [isOffsite, setIsOffsite] = useState(false);
  const [date, setDate] = useState("");
  const [startTime, setStartTime] = useState("10:00");
  const [endTime, setEndTime] = useState("14:00");
  const [equipmentIds, setEquipmentIds] = useState([]);
  const [crewRoles, setCrewRoles] = useState([]);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  // Feb 2026 · R268rrr · Persist guest name/email/phone in localStorage
  // scoped per-workspace (so a customer booking two different studios
  // doesn't cross-fill). Hydrated on mount + saved on change; keeps
  // repeat bookings 3 fewer taps away.
  const _guestStorageKey = `sb-guest:${wsId || shareSlug || "default"}`;
  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(_guestStorageKey);
      if (raw) {
        const g = JSON.parse(raw);
        if (g?.name && !name) setName(g.name);
        if (g?.email && !email) setEmail(g.email);
        if (g?.phone && !phone) setPhone(g.phone);
      }
    } catch { /* localStorage unavailable — fine */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_guestStorageKey]);
  useEffect(() => {
    try {
      if (!name && !email && !phone) return;
      window.localStorage.setItem(_guestStorageKey,
        JSON.stringify({ name, email, phone }));
    } catch { /* localStorage unavailable — fine */ }
  }, [name, email, phone, _guestStorageKey]);
  const [company, setCompany] = useState("");
  const [gstin, setGstin] = useState("");
  const [notes, setNotes] = useState("");
  const [locationNotes, setLocationNotes] = useState("");
  // Feb 2026 · Mandatory shoot brief — captured for every booking so the
  // production team has enough context before the shoot day.
  const [shootDetails, setShootDetails] = useState("");
  const [quote, setQuote] = useState(null);
  // Feb 2026 · R268yyy · Live-tease pulse — flashes the sticky pay bar
  // total whenever the quote refreshes (equipment toggle, crew change,
  // promo code) so the customer notices the price update from anywhere
  // on the page. Reset after the animation duration.
  const [pulseTotal, setPulseTotal] = useState(false);
  const prevTotalRef = useRef(null);
  useEffect(() => {
    const t = quote?.total ?? null;
    if (prevTotalRef.current !== null && t !== null && t !== prevTotalRef.current) {
      setPulseTotal(true);
      const id = setTimeout(() => setPulseTotal(false), 550);
      prevTotalRef.current = t;
      return () => clearTimeout(id);
    }
    prevTotalRef.current = t;
  }, [quote?.total]);
  const [avail, setAvail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [offers, setOffers] = useState([]);
  // Feb 2026 · Tracks the last auto-applied promo code we toasted for
  // so switching slot / studio / package doesn't re-fire the sticky
  // "you saved!" toast on every quote recompute.
  const autoPromoToastRef = useRef(null);
  // Feb 2026 · 1-minute tick for live offer countdowns. We re-render
  // every 60 s so the "Ends in 2d 14h" chip stays fresh without
  // forcing a per-second render budget on the browser.
  const [nowTick, setNowTick] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 60000);
    return () => clearInterval(id);
  }, []);
  const [promoCode, setPromoCode] = useState(initialPromo);
  const [promoStatus, setPromoStatus] = useState(null);
  const [promoMsg, setPromoMsg] = useState("");
  // Feb 2026 · Additional services (multi-select) that spawn an enquiry
  // on booking submit. Customer is clearly told a separate quote will
  // follow so they don't expect it to appear in the current total.
  const [additionalServices, setAdditionalServices] = useState([]);
  const [servicesCatalog, setServicesCatalog] = useState([]);

  // Feb 2026 · Fire a funnel event. Fire-and-forget, never blocks the UI.
  const track = (event) => {
    if (!wsId) return;
    api.post(`/public/studio/widget/${wsId}/track`, { event, slug: shareSlug })
       .catch(() => {});
  };

  // Step transitions map 1→step_datetime, 2→step_addons, 3→step_checkout.
  // Wrapped setStep so any caller (Continue buttons, space-tile clicks,
  // stepper pills) records the event without needing bespoke wiring.
  const STEP_EVENT = { 2: "step_datetime", 3: "step_addons", 4: "step_checkout" };
  const goStep = (n) => {
    if (n > step && STEP_EVENT[n]) track(STEP_EVENT[n]);
    setStep(n);
  };

  useEffect(() => {
    if (!urlParam) return;
    // Feb 2026 · R268mmm · Try UUID path first; on 404 fall back to slug.
    const loadByPath = async () => {
      const fetchPromo = (resolvedId) => {
        api.get(`/public/studio/widget/${resolvedId}/active-offers`).then((r) => setOffers(r.data.offers || [])).catch(() => {});
        api.get(`/public/studio-services/${resolvedId}`).then((r) => setServicesCatalog(r.data.services || [])).catch(() => {});
        api.post(`/public/studio/widget/${resolvedId}/track`, { event: "view", slug: shareSlug }).catch(() => {});
      };
      try {
        const r = isUuidLike
          ? await api.get(`/public/studio/widget/${urlParam}`)
          : await api.get(`/public/studio/widget/by-slug/${urlParam}`);
        setCfg(r.data);
        setWsId(r.data?.workspace?.id || (isUuidLike ? urlParam : null));
        fetchPromo(r.data?.workspace?.id || urlParam);
      } catch (e) {
        // If UUID-shaped path failed, try slug fallback (handles copy/paste with trailing chars)
        if (isUuidLike) {
          toast.error(apiErrorMessage(e, "Load failed"));
        } else {
          toast.error(apiErrorMessage(e, "No booking page with that URL"));
        }
      }
    };
    loadByPath();
    // eslint-disable-next-line
  }, [urlParam]);

  const wc = cfg?.widget_config || {};
  // R268sss · Feb 2026 · Compact layout mode. URL `?mode=compact`
  // ALWAYS wins over the widget's admin default; `?mode=steps` forces
  // the classic multi-step layout even if the admin picked compact.
  // R268uuu · Runtime layout override — customer can flip between
  // stepped and compact via a small toggle at the top of the widget.
  // State is preserved across the switch (nothing is reset).
  const [layoutOverride, setLayoutOverride] = useState(null); // null | "steps" | "compact"
  const layoutMode = (() => {
    if (layoutOverride === "compact" || layoutOverride === "steps") return layoutOverride;
    if (_urlMode === "compact" || _urlMode === "steps") return _urlMode;
    return (wc.layout_mode === "compact") ? "compact" : "steps";
  })();
  // R268yyy · Feb 2026 · Compact header smart-hide. On tall phones the
  // sticky header competes with the sticky pay bar for vertical real
  // estate, so we hide it on scroll-DOWN (past 80px) and bring it back
  // on any scroll-UP. Uses rAF-throttled scroll listener to stay
  // smooth on low-end Androids.
  const [headerHidden, setHeaderHidden] = useState(false);
  useEffect(() => {
    if (layoutMode !== "compact") return;
    let lastY = window.scrollY || 0;
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        const y = window.scrollY || 0;
        const delta = y - lastY;
        if (Math.abs(delta) > 8) {
          if (delta > 0 && y > 80) setHeaderHidden(true);
          else if (delta < 0)      setHeaderHidden(false);
          lastY = y;
        }
        ticking = false;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [layoutMode]);
  // R268yyy · Feb 2026 · Compact mode is now a true single-page form
  // (all four sections stacked and always visible after the customer
  // picks an entry tile). Before this, "compact" only stripped hero
  // chrome but the customer still walked steps 1 → 2 → 3 → 4. That
  // defeated the whole "quick, no-back-and-forth booking" intent.
  const showAllSteps = layoutMode === "compact" && (!wc.show_entry_chooser || entryMode);
  const toggleLayout = () => {
    setLayoutOverride(layoutMode === "compact" ? "steps" : "compact");
  };
  // R268xxx · Feb 2026 · goHome — soft reset of the flow back to step 1
  // (entry chooser / studio picker). Guest name/email/phone are
  // intentionally preserved because localStorage rehydrates them on
  // the next render anyway; wiping them would defeat that UX.
  const goHome = () => {
    setDone(null);
    setStep(1);
    setEntryMode(null);
    setStudioId(null); setSpaceId(null); setPackageId(null);
    setIsOffsite(false);
    setDate(""); setStartTime("10:00"); setEndTime("14:00");
    setEquipmentIds([]); setCrewRoles([]);
    setNotes(""); setLocationNotes(""); setShootDetails("");
    setQuote(null); setAvail(null);
    setPromoCode(""); setPromoStatus(null); setPromoMsg("");
    setAdditionalServices([]);
    try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch {}
  };
  // R268sss · Feb 2026 · Apply URL-driven pre-selection ONCE after
  // cfg loads. We only *pre-fill* the state — the customer can still
  // change any of it. Used for paid-ad campaigns that link straight
  // to the pay page with everything pre-selected.
  const _appliedDeepLink = useRef(false);
  useEffect(() => {
    if (!cfg || _appliedDeepLink.current) return;
    _appliedDeepLink.current = true;
    // Skip the entry chooser if `?entry=` is set.
    if (_urlEntry === "studio") setEntryMode("studio");
    else if (_urlEntry === "offsite") { setEntryMode("offsite"); setIsOffsite(true); }
    else if (_urlEntry) setEntryMode(_urlEntry);
    // Pre-select studio / space.
    let advanced = false;
    if (_urlSpace) {
      const sp = (cfg.spaces || []).find((s) => s.id === _urlSpace);
      if (sp) {
        setSpaceId(sp.id);
        if (sp.studio_id) setStudioId(sp.studio_id);
        advanced = true;
      }
    } else if (_urlStudio) {
      const st = (cfg.studios || []).find((s) => s.id === _urlStudio);
      if (st) { setStudioId(st.id); advanced = true; }
    }
    if (_urlDate) setDate(_urlDate);
    if (_urlStart) setStartTime(_urlStart);
    if (_urlEnd) setEndTime(_urlEnd);
    if (advanced) setStep(2);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg]);
  const _abs = (u) => (u && u.startsWith("/api/")) ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u;
  const [isMobile, setIsMobile] = useState(typeof window !== "undefined" && window.innerWidth < 640);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const on = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);
  const heroUrl = _abs((isMobile && wc.hero_image_url_mobile) ? wc.hero_image_url_mobile : wc.hero_image_url);
  const logoUrl = _abs(wc.logo_url);
  const primary = wc.primary_color || "#EA5934";
  const accent  = wc.accent_color  || "#0B2545";
  // Feb 2026 · R268jjj · Typography — resolve curated font slugs into
  // css font-family stacks + auto-inject the matching Google Fonts
  // stylesheet. `heading_font` styles the big hero + section headings;
  // `body_font` styles the flowing body copy.
  const headingSlug = wc.heading_font || "cormorant";
  const bodySlug    = wc.body_font    || "system";
  useGoogleFonts([headingSlug, bodySlug]);
  const serifFamily = FONT_STACKS[headingSlug] || FONT_STACKS.cormorant;
  const font = wc.font_family || FONT_STACKS[bodySlug] || FONT_STACKS.system;
  const showPackages  = wc.show_packages  !== false;
  const showEquipment = wc.show_equipment !== false;
  const showCrew      = wc.show_crew      !== false;
  const showOffsite   = wc.show_offsite   !== false;
  const reqPhone   = wc.require_phone   !== false;
  const reqCompany = !!wc.require_company;
  const reqGstin   = !!wc.require_gstin;

  const spacesForStudio = useMemo(() => {
    if (!cfg) return [];
    if (!studioId) return cfg.spaces || [];
    return (cfg.spaces || []).filter((s) => s.studio_id === studioId);
  }, [cfg, studioId]);
  const currentStudio = useMemo(
    () => (cfg?.studios || []).find((s) => s.id === studioId) || null,
    [cfg, studioId]
  );
  const currentSpace = useMemo(
    () => (cfg?.spaces || []).find((s) => s.id === spaceId) || null,
    [cfg, spaceId]
  );
  // Feb 2026 · Effective amenities for the picked space — resolved
  // server-side (`amenities_effective`) so studio → space inheritance
  // matches the settings preview. Legacy string-arrays are normalised
  // to objects so a workspace that predates the parent-list rollout
  // still renders correctly.
  const currentAmenities = useMemo(() => {
    if (!currentSpace || isOffsite) return [];
    const eff = currentSpace.amenities_effective;
    if (Array.isArray(eff) && eff.length > 0) return eff;
    if (Array.isArray(currentSpace.amenities)) {
      return currentSpace.amenities.map((a) => (typeof a === "string" ? { name: a } : a));
    }
    return [];
  }, [currentSpace, isOffsite]);

  const slot_start = date ? `${date}T${startTime}:00` : null;
  const slot_end = date ? `${date}T${endTime}:00` : null;

  useEffect(() => {
    if (!date || !slot_start || !slot_end) return;
    if (!spaceId && !isOffsite && equipmentIds.length === 0 && !packageId) return;
    api.post(`/public/studio/widget/${wsId}/quote`, {
      space_id: isOffsite ? null : spaceId,
      is_offsite: isOffsite,
      slot_start, slot_end,
      equipment_item_ids: equipmentIds,
      crew_roles: crewRoles,
      package_id: packageId,
      promo_code: promoCode || null,
    }).then((r) => {
      setQuote(r.data);
      // Feb 2026 · Reflect whether the entered code actually applied.
      if (promoCode) {
        if (r.data?.promo) {
          setPromoStatus("valid");
          setPromoMsg(`Promo ${r.data.promo.code} applied — you saved ₹${r.data.promo.discount?.toFixed(2)}`);
        } else {
          setPromoStatus("invalid");
          setPromoMsg("Code not valid for this booking");
        }
      } else {
        setPromoStatus(null); setPromoMsg("");
      }
      // Feb 2026 · Sticky "you just saved!" toast when the backend
      // auto-applies a promo (customer never typed a code). Fires
      // once per distinct auto-promo per session so switching slots
      // doesn't spam the toast. Uses the subtotal from the quote to
      // show both the amount AND the % saved — the customer feels
      // the win immediately before they even see the price breakdown.
      try {
        const auto = r.data?.promo?.auto_applied && !promoCode;
        if (auto) {
          const code = r.data.promo.code;
          if (autoPromoToastRef.current !== code) {
            autoPromoToastRef.current = code;
            const disc = Number(r.data.promo.discount || 0);
            const sub = Number(r.data.subtotal || 0) + disc;
            const pct = sub > 0 ? Math.round((disc / sub) * 100) : 0;
            const amount = `₹${disc.toFixed(0)}`;
            toast.success(
              `🎉 ${code} auto-applied — you just saved ${amount}${pct ? ` (${pct}% off)` : ""}`,
              { duration: Infinity, id: `auto-promo-${code}`,
                description: "No code needed. Discount shown in your quote.",
                className: "lx-auto-promo-toast" }
            );
          }
        } else if (!r.data?.promo) {
          autoPromoToastRef.current = null;
        }
      } catch (_) { /* non-fatal — toast UI only */ }
    }).catch(() => setQuote(null));
    // eslint-disable-next-line
  }, [spaceId, packageId, isOffsite, date, startTime, endTime, equipmentIds, crewRoles, promoCode]);

  // Feb 2026 · Probe availability inline so customers see off-hours / conflicts
  // BEFORE they hit Book. Runs whenever the slot changes.
  // Oct 2026 · Decouple studio-level availability from per-equipment
  // availability. The backend returns conflicts for each equipment id
  // we pass in — we send the whole catalog so unavailable items can be
  // flagged BEFORE the customer picks them, and so the booking is
  // never blocked just because one accessory is double-booked.
  useEffect(() => {
    if (!date || !slot_start || !slot_end) { setAvail(null); return; }
    if (!spaceId && !isOffsite && (cfg?.equipment || []).length === 0) { setAvail(null); return; }
    let cancelled = false;
    const allEquipmentIds = (cfg?.equipment || []).map((e) => e.id);
    api.post(`/public/studio/widget/${wsId}/check-availability`, {
      space_id: isOffsite ? null : spaceId,
      is_offsite: isOffsite,
      slot_start, slot_end,
      equipment_item_ids: allEquipmentIds,
    }).then((r) => { if (!cancelled) setAvail(r.data); })
      .catch(() => { if (!cancelled) setAvail(null); });
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [spaceId, isOffsite, date, startTime, endTime, cfg?.equipment?.length]);

  // Oct 2026 · Set of equipment ids that are unbookable for the
  // currently-selected slot — merged from the two sources the backend
  // returns: `conflicts.equipment` (double-booked by an existing
  // booking) and `conflicts.equipment_off_hours` (outside the item's
  // own open hours / inactive / blackout). An empty set means every
  // catalog item is pickable.
  const unavailableEquipmentIds = useMemo(() => {
    const s = new Set();
    if (!avail || !avail.conflicts) return s;
    Object.keys(avail.conflicts.equipment || {}).forEach((eid) => s.add(eid));
    (avail.conflicts.equipment_off_hours || []).forEach((o) => {
      if (o?.equipment_id) s.add(o.equipment_id);
    });
    return s;
  }, [avail]);

  // Oct 2026 · Keep `equipmentIds` tight to what's actually bookable
  // for the current slot. If the slot moves and now blocks something
  // the customer picked earlier, silently drop it so the quote stays
  // correct — the tile itself shows a red "not available" badge so
  // the drop isn't invisible.
  useEffect(() => {
    if (unavailableEquipmentIds.size === 0) return;
    setEquipmentIds((ids) => {
      const next = ids.filter((id) => !unavailableEquipmentIds.has(id));
      return next.length === ids.length ? ids : next;
    });
  }, [unavailableEquipmentIds]);

  // Oct 2026 · Studio-only block vs equipment-only block. We only
  // disable BOOK NOW and show the big red banner when the *studio*
  // itself can't take this slot (space clash or hours blocked).
  // Equipment conflicts surface inline on each tile instead.
  const studioBlocked = useMemo(() => {
    if (!avail || avail.available !== false) return false;
    const c = avail.conflicts || {};
    return (c.space || []).length > 0 || (c.off_hours || []).length > 0;
  }, [avail]);

  // Feb 2026 · R268lll · Field-highlight state — collects keys of the
  // fields that failed validation on the last Book-Now click so we can
  // flash a red outline + shake animation. Cleared as soon as the user
  // types into the offending field.
  const [invalidFields, setInvalidFields] = useState(new Set());
  const clearInvalid = (key) => {
    if (!invalidFields.has(key)) return;
    const next = new Set(invalidFields);
    next.delete(key);
    setInvalidFields(next);
  };
  const outlineIf = (key) => (invalidFields.has(key) ? "2px solid #dc2626" : undefined);

  const canBook = () => {
    if (!email || !name) return false;
    if (reqPhone && !phone) return false;
    if (reqCompany && !company) return false;
    if (reqGstin && !gstin) return false;
    // Oct 2026 · Only the studio-level block disables booking;
    // equipment conflicts are already filtered out of `equipmentIds`.
    if (studioBlocked) return false;
    return !!quote;
  };

  const book = async () => {
    // Feb 2026 · R268lll · Collect ALL invalid fields upfront (instead
    // of bailing on the first) so the user can fix them in one pass.
    const bad = new Set();
    if (!name) bad.add("name");
    if (!email) bad.add("email");
    if (reqPhone && !phone) bad.add("phone");
    if (reqCompany && !company) bad.add("company");
    if (reqGstin && !gstin) bad.add("gstin");
    if (isOffsite && !locationNotes.trim()) bad.add("location");
    if (!shootDetails.trim()) bad.add("shootDetails");
    if (bad.size > 0) {
      setInvalidFields(bad);
      // Auto-scroll to first missing field
      const firstKey = Array.from(bad)[0];
      const idMap = {
        name: "widget-name", email: "widget-email", phone: "widget-phone",
        company: "widget-company", gstin: "widget-gstin",
        location: "widget-location",
        shootDetails: "widget-shoot-details-step4",
      };
      setTimeout(() => {
        const el = document.querySelector(`[data-testid="${idMap[firstKey]}"]`);
        el?.focus();
        el?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 100);
      toast.error(`Please fill ${bad.size} required field${bad.size === 1 ? "" : "s"} highlighted below.`);
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/public/studio/widget/${wsId}/book`, {
        space_id: isOffsite ? null : spaceId,
        is_offsite: isOffsite,
        slot_start, slot_end,
        equipment_item_ids: equipmentIds,
        crew_roles: crewRoles,
        package_id: packageId,
        customer_name: name, customer_email: email, customer_phone: phone,
        customer_company: company, customer_gstin: gstin,
        notes: shootDetails.trim() ? `Shoot brief: ${shootDetails.trim()}${notes ? `\n\n${notes}` : ""}` : notes,
        location_notes: locationNotes,
        shoot_details: shootDetails,
        promo_code: promoCode || null,
      });
      setDone(data);
      track("step_paid");
      // Feb 2026 · If the customer picked additional services, spawn an
      // enquiry so the studio team can prepare a separate quote. Best
      // effort — a failure here must not block the successful booking.
      if (additionalServices.length > 0) {
        try {
          await api.post(`/public/enquiries/${wsId}`, {
            type_slug: "custom-shoot",
            customer_name: name, customer_email: email, customer_phone: phone,
            company, message: `Additional services requested with booking ${data?.booking?.code}: ${additionalServices.join(", ")}.\n\n${notes || ""}`,
            source: "studio_booking",
            create_lead: true,
            linked_booking_id: data?.booking?.id,
            linked_services: additionalServices,
          });
        } catch (_e) { /* silently */ }
      }
      // Feb 2026 · Cashfree hosted checkout — invoke the JS SDK with
      // the server-issued payment_session_id + mode. The SDK opens
      // Cashfree's hosted page and redirects the customer back to
      // /pay/return?order_id=... on completion.
      const pay = data?.payment || {};
      // R268uuu · Feb 2026 · UPI-QR direct-collection mode.
      // When the parent studio is configured for `upi_qr`, the
      // backend returns `provider: "upi_qr"` with the QR image URL
      // + upi_id — we skip the Cashfree SDK entirely and render the
      // QR + proof-upload form inside the done screen.
      if (pay?.provider === "upi_qr") {
        // Nothing to do here — the BookingDoneScreen renders the QR
        // panel automatically based on data.payment.provider.
      } else if (pay?.configured && pay?.payment_session_id) {
        try {
          const cashfree = await loadCashfree({
            mode: pay.mode === "LIVE" ? "production" : "sandbox",
          });
          if (cashfree) {
            const origin = window.location.origin;
            const orderId = pay.order_id;
            const returnUrl = pay.return_url ||
              `${origin}/pay/return?order_id=${encodeURIComponent(orderId || "")}`;
            const result = await cashfree.checkout({
              paymentSessionId: pay.payment_session_id,
              returnUrl,
            });
            if (result?.error) {
              toast.error(result.error.message || "Payment could not be started");
            }
          } else {
            toast.error("Payment SDK failed to load. You'll receive an email to complete payment.");
          }
        } catch (sdkErr) {
          toast.error("Payment window could not open. Please retry.");
        }
      } else if (pay && !pay.configured) {
        // Feb 2026 · R268qqq · Surface the real Cashfree error to the
        // operator instead of the misleading "gateway not configured"
        // message. When `pay.error` is present the studio can see the
        // exact Cashfree failure (bad keys, wrong customer_id, 400
        // body etc); otherwise fall back to the legacy toast.
        if (pay.error) {
          console.error("Cashfree payment start failed", pay);
          toast.error(`Payment couldn't start: ${pay.error}. Our team has been notified.`, { duration: 12000 });
        } else {
          toast.info("Booking received — payment gateway is not configured yet, our team will reach out.");
        }
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.msg === "Not available") {
        toast.error("Selected slot is not available — please pick another time");
      } else {
        toast.error(apiErrorMessage(e, "Booking failed"));
      }
    } finally { setBusy(false); }
  };

  if (!cfg) return <div style={{ padding: 40, fontFamily: font }}>Loading…</div>;
  // Feb 2026 · Warm-luxe design tokens — soft creams, deep primary,
  // subtle motion. Colors are intentionally warm-neutral so any brand
  // primary (deep green, burgundy, midnight blue) looks luxurious on
  // top; workspace primary/accent still override where used.
  const cream100 = "#FBF7F0";
  const cream200 = "#F3ECE0";
  // Feb 2026 · R268jjj · Palette overrides — admin picks text +
  // heading colors from the widget config; defaults preserve the
  // warm-luxe bark tones.
  const bark800 = wc.heading_color || "#241A11";
  const bark600 = wc.text_color    || "#5C4A38";
  const gold = "#B8860B";
  const containerStyle = { maxWidth: 1040, margin: "0 auto", padding: "20px 18px 96px", fontFamily: font, color: bark800 };

  if (done) return (
    <BookingDoneScreen
      done={done} wsId={wsId} shareSlug={shareSlug}
      date={date} startTime={startTime} endTime={endTime}
      currentStudio={currentStudio} currentSpace={currentSpace}
      currentAmenities={currentAmenities} isOffsite={isOffsite}
      wc={wc} logoUrl={logoUrl} font={font} serifFamily={serifFamily}
      bark800={bark800} bark600={bark600} cream100={cream100}
      cream200={cream200} gold={gold} primary={primary}
      onNewBooking={() => {
        // Feb 2026 · R268qqq · Reset the booking flow so the customer
        // can immediately book another slot without a full reload.
        // R268rrr · Preserve guest name/email/phone so repeat customers
        // don't have to retype who they are — those come back from
        // localStorage on the next hydrate anyway.
        setDone(null);
        setStep(1);
        setEntryMode(null);
        setStudioId(null); setSpaceId(null); setPackageId(null);
        setIsOffsite(false);
        setDate(""); setStartTime("10:00"); setEndTime("14:00");
        setEquipmentIds([]); setCrewRoles([]);
        // NOTE: name / email / phone / company / gstin intentionally
        // NOT cleared — repeat bookings are one of the top drop-off
        // points and pre-filling those is the biggest single win.
        setNotes(""); setLocationNotes(""); setShootDetails("");
        setQuote(null); setAvail(null);
        setPromoCode(""); setPromoStatus(null); setPromoMsg("");
        setAdditionalServices([]);
        try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch {}
      }}
    />
  );

  return (
    <div className={[
          layoutMode === "compact" ? "lx-compact" : "",
          (showAllSteps && !done) ? "lx-has-sticky-pay" : "",
        ].filter(Boolean).join(" ")}
          style={{ minHeight: "100vh", background: `linear-gradient(180deg, ${cream100} 0%, #FFFFFF 60%)`, fontFamily: font, color: bark800 }} data-testid="widget-root">
      <style>{luxeInlineStyles}</style>
      {/* Hero — full-bleed image with a soft cream gradient overlay for legibility.
          R268sss · Feb 2026 · Hidden in `compact` mode for repeat customers /
          paid-ad landings so nothing sits between them and the form. */}
      {layoutMode !== "compact" && (
      <div className="lx-hero" style={{
        position: "relative",
        background: heroUrl
          ? `linear-gradient(180deg, rgba(20,12,6,.35) 0%, rgba(20,12,6,.55) 100%), url(${heroUrl}) center/cover`
          : `linear-gradient(160deg, ${primary} 0%, ${accent} 100%)`,
        color: "white", padding: "72px 24px 96px", textAlign: "center",
        overflow: "hidden",
      }}>
        {/* R268xxx · Home icon in the top-left of the hero — customer
            can bail back to the booking landing at any step. Uses a
            subtle glass-morphism styling so it doesn't compete with
            the hero's headline. */}
        <button type="button" onClick={goHome}
          data-testid="widget-home-btn"
          className="lx-hero-home"
          title="Back to booking home"
          aria-label="Back to booking home"
          style={{
            position: "absolute", top: 20, left: 20,
            background: "rgba(255,255,255,0.14)", color: "white",
            border: "1px solid rgba(255,255,255,0.35)",
            padding: "8px 12px", cursor: "pointer",
            fontSize: 14, backdropFilter: "blur(6px)",
            display: "flex", alignItems: "center", gap: 6, zIndex: 2,
          }}>
          🏠 <span style={{ fontSize: 10, letterSpacing: 2, textTransform: "uppercase" }}>Home</span>
        </button>
        {/* Subtle grain overlay */}
        <div style={{ position: "absolute", inset: 0,
                        background: "radial-gradient(rgba(255,255,255,0.03) 1px, transparent 1px)",
                        backgroundSize: "3px 3px", pointerEvents: "none" }}></div>
        <div className="lx-hero-inner" style={{ position: "relative", maxWidth: 760, margin: "0 auto" }}>
          {logoUrl && <img src={logoUrl} alt="logo" style={{ maxHeight: 44, marginBottom: 20 }} loading="eager" decoding="async" />}
          {!wc.hide_workspace_name && (
            <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 4, opacity: 0.85, fontWeight: 600 }}
                  data-testid="widget-eyebrow">
              {wc.title || cfg.workspace?.name}
            </div>
          )}
          <h1 className="lx-hero-h1"
              style={{ fontFamily: serifFamily, fontSize: "clamp(30px, 5.5vw, 56px)",
                        margin: "12px 0 0", fontWeight: 500, letterSpacing: -0.5,
                        WebkitFontSmoothing: "antialiased" }}>
            {wc.tagline || "Reserve your session"}
          </h1>
          {wc.subtitle && <div style={{ maxWidth: 560, margin: "18px auto 0", fontSize: 15, opacity: 0.92, fontWeight: 400, lineHeight: 1.55 }}>{wc.subtitle}</div>}
          {wc.welcome_text && <div style={{ maxWidth: 560, margin: "12px auto 0", fontSize: 13, opacity: 0.85 }} dangerouslySetInnerHTML={{ __html: wc.welcome_text }} />}
          {/* R268uuu · Feb 2026 · Runtime layout toggle — repeat
              customers can flip to the compact single-page form
              anytime, and their in-progress state is preserved. */}
          <button type="button" onClick={toggleLayout}
            data-testid="widget-layout-toggle"
            style={{
              marginTop: 20, background: "rgba(255,255,255,0.14)",
              color: "white", border: "1px solid rgba(255,255,255,0.35)",
              padding: "6px 14px", fontSize: 10, letterSpacing: 2,
              textTransform: "uppercase", cursor: "pointer",
              backdropFilter: "blur(6px)",
            }}>
            ↕ Switch to compact view
          </button>
        </div>
      </div>
      )}
      {/* Compact mode header — sticky at top so the studio identity,
           call, and WhatsApp shortcuts stay reachable from any section
           on the single-page booking flow. */}
      {layoutMode === "compact" && (() => {
        const call = currentStudio?.mobile || currentStudio?.phone || "";
        const wa   = currentStudio?.whatsapp || currentStudio?.mobile || currentStudio?.phone || "";
        const norm = (n) => (n || "").replace(/[^\d+]/g, "");
        const waUrl = wa
          ? `https://wa.me/${norm(wa).replace(/^\+/, "")}?text=${encodeURIComponent(`Hi, I'm booking ${currentStudio?.name || ""}.`)}`
          : "";
        // Oct 2026 · Directions deep-link in the sticky compact
        // header. Keeps the icon-pill style so narrow widths stay neat.
        const dirUrl = (() => {
          const s = currentStudio || {};
          if (s.map_url) return s.map_url;
          if (s.latitude != null && s.longitude != null) return `https://www.google.com/maps/?q=${s.latitude},${s.longitude}`;
          const addr = [s.address, s.city, s.state, s.pincode, s.country].filter(Boolean).join(", ");
          return addr ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr)}` : "";
        })();
        // Prefer the *selected studio's* logo — falls back to the
        // workspace-wide logo so the header never renders empty.
        // Oct 2026 — if the workspace only has one studio, use that
        // studio's logo even before the customer picks a space.
        const headerLogo = (currentStudio?.logo_url ? _absPub(currentStudio.logo_url) : "")
          || ((cfg?.studios?.length === 1 && cfg.studios[0]?.logo_url) ? _absPub(cfg.studios[0].logo_url) : "")
          || logoUrl;
        // Oct 2026 polish — show the studio name even BEFORE a space
        // is picked. If the workspace has exactly one studio, use it;
        // otherwise fall back to the configured widget title /
        // workspace name so the sticky header always carries brand.
        const soleStudio = (cfg?.studios?.length === 1) ? cfg.studios[0] : null;
        const headerName = currentStudio?.name || soleStudio?.name || wc.title || cfg.workspace?.name || "Reserve your session";
        return (
        <div style={{
          background: "rgba(255,255,255,0.96)", backdropFilter: "blur(10px)",
          borderBottom: `1px solid ${cream200}`, padding: "12px 20px",
          position: "sticky", top: 0, zIndex: 45,
          boxShadow: "0 4px 12px -8px rgba(36,26,17,0.14)",
          transform: headerHidden ? "translateY(-110%)" : "translateY(0)",
          transition: "transform 260ms ease",
          willChange: "transform",
        }}
              data-testid="widget-compact-header">
          <div style={{ maxWidth: 760, margin: "0 auto", display: "flex", alignItems: "center", gap: 10 }}>
            <button type="button" onClick={goHome}
              data-testid="widget-home-btn"
              title="Back to booking home"
              style={{ background: "transparent", border: `1px solid ${cream200}`,
                        padding: "6px 8px", cursor: "pointer", color: bark600,
                        display: "flex", alignItems: "center", justifyContent: "center" }}
              aria-label="Back to booking home">
              🏠
            </button>
            {headerLogo && (
              <img src={headerLogo} alt="logo" data-testid="widget-header-logo"
                    style={{ maxHeight: 32, maxWidth: 90, objectFit: "contain" }}
                    loading="eager" decoding="async" />
            )}
            <div style={{ fontFamily: serifFamily,
                            fontSize: "clamp(12px, 3.5vw, 16px)",
                            fontWeight: 500, color: bark800,
                            flex: 1, minWidth: 0, overflow: "hidden",
                            textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                  title={headerName}
                  data-testid="widget-header-name">
              {headerName}
            </div>
            {/* Feb 2026 · Quick contact icons — visible whenever the
                 selected studio has a phone / WA number. Kept as
                 icon-only pills so they don't crowd the header at
                 narrow widths. */}
            {call && (
              <a href={`tel:${norm(call)}`} data-testid="widget-header-call"
                  title={`Call ${call}`}
                  aria-label={`Call ${call}`}
                  style={{ display: "inline-flex", alignItems: "center", justifyContent: "center",
                            width: 34, height: 34, borderRadius: 999,
                            background: "white", border: `1px solid ${primary}`, color: primary,
                            textDecoration: "none", flexShrink: 0 }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                      stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
                </svg>
              </a>
            )}
            {dirUrl && (
              <a href={dirUrl} target="_blank" rel="noopener noreferrer"
                  data-testid="widget-header-directions"
                  title="Get directions"
                  aria-label="Get directions"
                  style={{ display: "inline-flex", alignItems: "center", justifyContent: "center",
                            width: 34, height: 34, borderRadius: 999,
                            background: "white", border: `1px solid ${primary}`, color: primary,
                            textDecoration: "none", flexShrink: 0, fontSize: 16, lineHeight: 1 }}>
                📍
              </a>
            )}
            {waUrl && (
              <a href={waUrl} target="_blank" rel="noopener noreferrer"
                  data-testid="widget-header-wa"
                  title={`WhatsApp ${wa}`}
                  aria-label={`WhatsApp ${wa}`}
                  style={{ display: "inline-flex", alignItems: "center", justifyContent: "center",
                            width: 34, height: 34, borderRadius: 999,
                            background: "#25D366", border: "1px solid #25D366", color: "white",
                            textDecoration: "none", flexShrink: 0 }}>
                {/* Simplified WhatsApp glyph */}
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                  <path d="M20.52 3.449C18.24 1.245 15.24 0 12.045 0 5.463 0 .104 5.334.101 11.893c0 2.096.549 4.14 1.595 5.945L0 24l6.335-1.652a11.882 11.882 0 0 0 5.723 1.467h.006c6.585 0 11.946-5.336 11.949-11.896 0-3.176-1.24-6.165-3.493-8.42zm-8.474 18.297h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.976 1.005-3.638-.235-.374a9.86 9.86 0 0 1-1.511-5.27c.001-5.45 4.436-9.884 9.888-9.884 2.641 0 5.122 1.03 6.988 2.898 1.867 1.869 2.893 4.352 2.892 6.993-.002 5.45-4.437 9.891-9.888 9.891z"/>
                </svg>
              </a>
            )}
            <button type="button" onClick={toggleLayout}
              data-testid="widget-layout-toggle"
              style={{ background: "transparent", border: `1px solid ${cream200}`,
                        padding: "6px 10px", fontSize: 9, letterSpacing: 1.5,
                        textTransform: "uppercase", color: bark600, cursor: "pointer",
                        flexShrink: 0 }}>
              ↕ Full
            </button>
          </div>
        </div>
        );
      })()}

      <div className="lx-container" style={{ ...containerStyle, marginTop: layoutMode === "compact" ? 0 : -48 }}>
        {/* Card wrapping the flow so it lifts off the hero */}
        <div className="lx-card" style={{ background: "white",
                        padding: layoutMode === "compact" ? "18px 20px 110px" : "28px 28px 40px",
                        boxShadow: layoutMode === "compact" ? "none" : "0 24px 60px -20px rgba(36,26,17,0.18)",
                        border: layoutMode === "compact" ? "none" : `1px solid ${cream200}`,
                        borderTop: layoutMode === "compact" ? "none" : `2px solid ${gold}` }}>
        {/* Sticky single-word stepper — hidden in compact mode.
             Feb 2026 · Also hidden on the landing tile chooser (before
             the customer picks a tile) so the Studio / Enquiry tiles
             own that screen. The stepper only makes sense once the
             actual studio booking flow has started (entryMode="studio"). */}
        {layoutMode !== "compact" && !(wc.show_entry_chooser && step === 1 && !entryMode && !done) && (
        <div className="lx-stepper-wrap">
          <div style={{ display: "flex", gap: 6 }}>
            {["Space", "Time", "Extras", "Pay"].map((t, i) => {
              const n = i + 1;
              const reached = step >= n;
              const past = step > n;
              const active = step === n;
              return (
                <button key={t} type="button"
                          onClick={() => past && !done && goStep(n)}
                          disabled={!past || !!done}
                          data-testid={`widget-step-pill-${n}`}
                          className="lx-step"
                          data-reached={reached ? "1" : "0"}
                          data-active={active ? "1" : "0"}
                          data-past={past && !done ? "1" : "0"}>
                  <span style={{ opacity: 0.6, marginRight: 4 }}>{n}</span>{t}
                </button>
              );
            })}
          </div>
        </div>
        )}

        {/* Feb 2026 · Active offers strip — under the stepper so it never
             crosses into the hero region on scroll or narrow viewports.
             Oct 2026 polish — explicit "Special offers" title so the
             coupon rail reads as a benefit section instead of random
             tiles. */}
        {offers.length > 0 && !done && step < 4 && (
          <div style={{ margin: "20px 0 24px" }} data-testid="widget-offers">
            <div
              style={{
                display: "flex", alignItems: "baseline", justifyContent: "space-between",
                marginBottom: 10, gap: 12,
              }}
              data-testid="widget-offers-title"
            >
              <span style={{ fontFamily: serifFamily, fontSize: 18, fontWeight: 500, color: bark800, letterSpacing: -0.2 }}>
                Special offers
              </span>
            </div>
            <div style={{ display: "flex", gap: 12, overflowX: "auto", padding: "4px 4px 12px", marginLeft: -4, marginRight: -4 }}
                 data-testid="widget-offers-strip">
            {offers.slice(0, 6).map((o) => {
              const label = o.type === "percent" ? `${o.value}% OFF` : `₹${o.value} OFF`;
              // Feb 2026 · Live-updating countdown chip replaces the
              // static "ENDS SOON" badge. Visible on any offer with an
              // `ends_at` within 7 days; styled brighter for the
              // last 24 hr; prominent countdown chip for auto-apply
              // offers so the customer feels the urgency.
              const countdown = _offerCountdownLabel(o.ends_at, nowTick);
              return (
                <div key={o.code} data-testid={`widget-offer-${o.code}`}
                     className="lx-offer"
                     style={{ flexShrink: 0, minWidth: 240,
                                background: `linear-gradient(135deg, ${cream100} 0%, #FFFFFF 100%)`,
                                borderLeft: `3px solid ${gold}`,
                                border: `1px solid ${cream200}`,
                                cursor: "pointer", transition: "all 0.24s ease",
                                position: "relative", overflow: "hidden" }}
                     onClick={() => { setPromoCode(o.code); toast.success(`Code ${o.code} pre-filled — continue to checkout to apply`); }}>
                  {/* Countdown ribbon spans the full width of the tile
                      so it never collides with the offer name text.
                      Auto-apply offers get a solid gold ribbon; manual
                      offers get a cream ribbon; last-day urgency
                      pulses in red to drive the booking decision. */}
                  {countdown && (
                    <div data-testid={`widget-offer-countdown-${o.code}`}
                           className={countdown.urgent ? "lx-offer-countdown-urgent" : ""}
                           style={{ fontSize: 9, fontFamily: "monospace", fontWeight: 700,
                                      padding: "3px 10px", letterSpacing: 1.4,
                                      textTransform: "uppercase",
                                      background: countdown.urgent ? "#C8302E"
                                                     : o.auto_apply ? gold
                                                     : cream200,
                                      color: (countdown.urgent || o.auto_apply) ? "#FFF" : bark800,
                                      borderBottom: `1px solid ${countdown.urgent ? "#C8302E" : o.auto_apply ? gold : cream200}`,
                                      whiteSpace: "nowrap", textAlign: "center" }}>
                      {countdown.urgent ? "⏱ " : ""}Ends in {countdown.label}
                    </div>
                  )}
                  <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 16px" }}>
                  <div style={{ fontFamily: serifFamily, fontSize: 24, fontWeight: 500, color: primary, whiteSpace: "nowrap", letterSpacing: -0.5 }}>{label}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: bark800 }}>
                      {o.name || o.code}
                    </div>
                    <div style={{ fontSize: 10, letterSpacing: 1.5, color: gold, fontFamily: "monospace", fontWeight: 700 }}>
                      {o.auto_apply ? "AUTO-APPLIED" : `USE ${o.code}`}
                    </div>
                  </div>
                  </div>
                </div>
              );
            })}
            </div>
          </div>
        )}

        {/* Feb 2026 · Back button — visible on any step > 1 (except after confirmation).
             Hidden in compact single-page mode since every section is already visible. */}
        {step > 1 && !done && !showAllSteps && (
          <button type="button" onClick={() => setStep(step - 1)}
                    style={{
                      padding: "8px 14px",
                      background: "transparent",
                      border: `1px solid ${bark600}`,
                      marginBottom: 20,
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: 1.5,
                      textTransform: "uppercase",
                      cursor: "pointer",
                      color: bark800,
                      transition: "background 120ms",
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = cream100; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                    data-testid="widget-back">
            ← Back to step {step - 1}
          </button>
        )}

        {/* Feb 2026 · Landing chooser. When `entry_tiles` is
            configured, we render the admin's fully custom tile list;
            otherwise we fall back to the legacy 3-tile chooser using
            the entry_*_label fields. `kind=studio` continues the
            booking flow; `kind=enquiry` deep-links to /enquire/…. */}
        {wc.show_entry_chooser && step === 1 && !entryMode && !done && (() => {
          const tiles = (Array.isArray(wc.entry_tiles) && wc.entry_tiles.length > 0)
            ? wc.entry_tiles
            : [
                { kind: "studio", label: wc.entry_studio_label || "Book a studio", description: "Pick a space, date, extras — pay online." },
                { kind: "enquiry", slug: "offsite-shoot", label: wc.entry_offsite_label || "Offsite shoot enquiry", description: "Tell us the brief — we'll send a tailored quote." },
                { kind: "enquiry", slug: wc.entry_other_type_slug || "custom-shoot", label: wc.entry_other_label || "Digital · design · production", description: "Any other request — briefs, quotes, custom." },
              ];
          const tileBase = { padding: 16, textAlign: "left", border: `1px solid ${cream200}`, background: "white", cursor: "pointer", transition: "all 0.2s", textDecoration: "none" };
          const headline = currentStudio?.name
            || (cfg?.studios?.length === 1 ? cfg.studios[0]?.name : "")
            || wc.title
            || cfg?.workspace?.name
            || "";
          return (
            <div data-testid="widget-entry-chooser" style={{ display: "grid", gap: 12 }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {headline && (
                  <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 3, color: gold, fontWeight: 700 }}
                        data-testid="widget-entry-studio-name">
                    {headline}
                  </div>
                )}
                <div style={{ fontFamily: serifFamily, fontSize: 20, fontWeight: 500, color: bark800, lineHeight: 1.2 }}
                      data-testid="widget-entry-headline">
                  Select a choice
                </div>
              </div>
              <div style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))" }}>
                {tiles.map((t, i) => {
                  const kind = t.kind || (t.slug ? "enquiry" : "studio");
                  const label = t.label || "";
                  const desc = t.description || "";
                  const testid = `widget-entry-${kind === "studio" ? "studio" : (t.slug || i)}`;
                  const commonProps = {
                    key: i,
                    style: tileBase,
                    onMouseEnter: (e) => { e.currentTarget.style.borderColor = primary; e.currentTarget.style.transform = "translateY(-2px)"; },
                    onMouseLeave: (e) => { e.currentTarget.style.borderColor = cream200; e.currentTarget.style.transform = "translateY(0)"; },
                    "data-testid": testid,
                  };
                  const inner = (
                    <>
                      <div style={{ fontFamily: serifFamily, fontSize: 19, fontWeight: 500, color: bark800, lineHeight: 1.15, marginBottom: 3 }}>{label}</div>
                      {desc && <div style={{ fontSize: 11, color: bark600, lineHeight: 1.35 }}>{desc}</div>}
                    </>
                  );
                  if (kind === "studio") {
                    return (
                      <button type="button" {...commonProps}
                              onClick={() => { setEntryMode("studio"); setIsOffsite(false); }}>
                        {inner}
                      </button>
                    );
                  }
                  return (
                    <a {...commonProps} href={`/enquire/${wsId}/${t.slug || "custom-shoot"}`} target="_self">
                      {inner}
                    </a>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {(step === 1 || showAllSteps) && (!wc.show_entry_chooser || entryMode) && (
          <div data-testid="widget-step-1">
            {/* Feb 2026 · R268nnn · Back-to-home link — when the
                landing chooser is enabled and the customer has picked
                a tile (entryMode set), give them a clear route back
                to swap to a different tile. Also resets step 1
                selections so nothing carries over stale. */}
            {wc.show_entry_chooser && entryMode && (
              <button type="button"
                       onClick={() => {
                         setEntryMode(null);
                         setStudioId(null);
                         setSpaceId(null);
                         setIsOffsite(false);
                       }}
                       style={{
                         padding: "8px 14px",
                         background: "transparent",
                         border: `1px solid ${bark600}`,
                         marginBottom: 20,
                         fontSize: 11,
                         fontWeight: 700,
                         letterSpacing: 1.5,
                         textTransform: "uppercase",
                         cursor: "pointer",
                         color: bark800,
                         transition: "background 120ms",
                       }}
                       onMouseEnter={(e) => { e.currentTarget.style.background = cream100; }}
                       onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                       data-testid="widget-back-to-home">
                ← Back to booking home
              </button>
            )}
            {/* R268bbb · Feb 2026 · The old "This is an offsite shoot"
                checkbox inside the studio flow was removed — offsite
                is now a dedicated entry via the landing chooser that
                jumps to `/enquire/{wsId}/offsite-shoot`. Keeping two
                offsite entry points confused customers and left
                bookings in an ambiguous state (part-studio, part-
                enquiry). */}

            {!isOffsite && (cfg.studios || []).length > 0 && !studioId && (
              <>
                <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 3, color: bark600, fontWeight: 700, marginBottom: 14 }}>1. Choose your studio</div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 16, marginBottom: 28 }}>
                  {cfg.studios.filter((s) => s.active !== false).map((s) => (
                    <button key={s.id} type="button" onClick={() => setStudioId(s.id)}
                            data-testid={`widget-studio-${s.id}`}
                            className="lx-space-tile"
                            style={{ border: `1px solid ${studioId === s.id ? gold : cream200}`,
                                       background: "white", textAlign: "left", cursor: "pointer",
                                       padding: 0, overflow: "hidden", position: "relative" }}>
                      {s.cover_image_url && <img src={_abs(s.cover_image_url)} alt="" style={{ width: "100%", height: 160, objectFit: "cover", display: "block" }} loading="lazy" decoding="async" />}
                      {/* Feb 2026 · R268kkk · Studio brand logo overlay
                          — shown on space tiles (top-left corner) when
                          there's a cover image. When no cover, we
                          render the logo INLINE next to the name so
                          it doesn't overlap the text on narrow phones
                          (R280.5 mobile fix). */}
                      {s.logo_url && s.cover_image_url && (
                        <img src={_abs(s.logo_url)} alt="" style={{
                          position: "absolute", top: 10, left: 10, height: 32, maxWidth: 96,
                          objectFit: "contain", background: "rgba(255,255,255,0.92)",
                          padding: "3px 6px", boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
                        }} />
                      )}
                      <div style={{ padding: "16px 18px", display: "flex", gap: 12, alignItems: "flex-start" }}>
                        {s.logo_url && !s.cover_image_url && (
                          <img src={_abs(s.logo_url)} alt="" style={{
                            height: 56, width: 56, minWidth: 56, objectFit: "contain",
                            background: cream100, padding: 4, border: `1px solid ${cream200}`, flexShrink: 0,
                          }} />
                        )}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div className="lx-space-serif" style={{ fontFamily: serifFamily, fontSize: 20, fontWeight: 500, color: bark800, letterSpacing: -0.3, lineHeight: 1.15, paddingBottom: 2, wordBreak: "break-word" }}>{s.name}</div>
                          {s.address && <div style={{ fontSize: 12, color: bark600, marginTop: 6, display: "flex", alignItems: "flex-start", gap: 4 }}>📍 <span>{s.address}</span></div>}
                          {(s.mobile || s.phone) && <div style={{ fontSize: 12, color: bark600, marginTop: 2 }}>☎ {s.mobile || s.phone}</div>}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </>
            )}

            {/* Feb 2026 · R268kkk · Persistent Call + WhatsApp CTA once
                a studio has been picked — shows on every step from
                here on out (Step 1 space-picker onwards). */}
            {!isOffsite && currentStudio && (
              <CallWhatsAppBar studio={currentStudio} primary={primary}
                                cream100={cream100} cream200={cream200} />
            )}

            {!isOffsite && currentStudio && (
              <div style={{ padding: 16, border: `1px solid ${cream200}`, borderLeft: `3px solid ${gold}`,
                              background: cream100, marginBottom: 20 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
                  {/* Feb 2026 · R268kkk · Studio logo shown on the
                      selected-studio hero card too, so branding is
                      consistent from tile → detail → booking. */}
                  {currentStudio.logo_url && (
                    <img src={_abs(currentStudio.logo_url)} alt=""
                          style={{ height: 56, maxWidth: 140, objectFit: "contain", flexShrink: 0 }} />
                  )}
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 3, color: gold, fontWeight: 700 }}>Selected studio</div>
                    <div style={{ fontFamily: serifFamily, fontSize: 22, fontWeight: 500, marginTop: 4, color: bark800, letterSpacing: -0.3 }}>{currentStudio.name}</div>
                    {currentStudio.address && <div style={{ fontSize: 12, color: bark600, marginTop: 4 }}>{currentStudio.address}</div>}
                    {currentStudio.directions_notes && <div style={{ fontSize: 12, color: bark600, marginTop: 6, fontStyle: "italic" }}>🚗 {currentStudio.directions_notes}</div>}
                    {(currentStudio.latitude != null && currentStudio.longitude != null) && (
                      <a href={currentStudio.map_url || `https://www.google.com/maps/?q=${currentStudio.latitude},${currentStudio.longitude}`}
                          target="_blank" rel="noopener noreferrer"
                          style={{ fontSize: 12, color: gold, marginTop: 6, display: "inline-block", fontWeight: 700, textDecoration: "none", borderBottom: `1px solid ${gold}` }}>
                        Open in Google Maps →
                      </a>
                    )}
                  </div>
                  <button onClick={() => { setStudioId(null); setSpaceId(null); }} style={{ background: "transparent", border: 0, color: bark600, cursor: "pointer", fontSize: 11, textTransform: "uppercase", letterSpacing: 2, fontWeight: 700 }}>Change</button>
                </div>
              </div>
            )}

            {/* Feb 2026 · R268kkk · Studio photo slideshow — auto-
                advances every 4s with dots + arrows. Only shows when
                the studio has ≥1 gallery photo. */}
            {!isOffsite && currentStudio && Array.isArray(currentStudio.gallery_urls) && currentStudio.gallery_urls.length > 0 && (
              <StudioSlideshow urls={currentStudio.gallery_urls}
                                logoUrl={currentStudio.logo_url}
                                alt={currentStudio.name}
                                gold={gold} bark800={bark800} cream200={cream200} />
            )}


            {!isOffsite && (studioId || (cfg.studios || []).length === 0) && (
              <>
                <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 3, color: bark600, fontWeight: 700, marginBottom: 14 }}>2. Pick a space</div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 16 }}>
                  {spacesForStudio.map((s) => (
                    <button key={s.id} type="button" onClick={() => { setSpaceId(s.id); goStep(2); }}
                            data-testid={`widget-space-${s.id}`}
                            className="lx-space-tile"
                            style={{ padding: 18, border: `1px solid ${spaceId === s.id ? gold : cream200}`,
                                       borderLeft: `3px solid ${spaceId === s.id ? gold : cream200}`,
                                       textAlign: "left", background: "white", cursor: "pointer" }}>
                      <div className="lx-space-serif" style={{ fontFamily: serifFamily, fontSize: 22, fontWeight: 500, letterSpacing: -0.3, color: bark800, lineHeight: 1.15, paddingBottom: 2 }}>{s.name}</div>
                      <div style={{ fontSize: 12, color: bark600, marginTop: 4, lineHeight: 1.5 }}>{s.description}</div>
                      {/* Feb 2026 · Effective amenities — computed server-side
                          from studio → space inheritance (`amenities_effective`).
                          Legacy `amenities` (list of strings) also handled for
                          older workspaces that predate the parent-list rollout. */}
                      {(() => {
                        const eff = Array.isArray(s.amenities_effective) && s.amenities_effective.length > 0
                          ? s.amenities_effective
                          : (Array.isArray(s.amenities) ? s.amenities.map((a) => typeof a === "string" ? { name: a } : a) : []);
                        if (eff.length === 0) return null;
                        return (
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 12 }}
                                data-testid={`widget-space-amenities-${s.id}`}>
                            {eff.slice(0, 6).map((a, i) => (
                              <span key={a?.id || i}
                                     title={a?.description || ""}
                                     style={{ fontSize: 9, textTransform: "uppercase", letterSpacing: 1.5,
                                                background: cream100, padding: "3px 8px", color: bark600, fontWeight: 600 }}>
                                {a?.name || ""}
                              </span>
                            ))}
                            {eff.length > 6 && (
                              <span style={{ fontSize: 10, color: bark600 }}>+{eff.length - 6}</span>
                            )}
                          </div>
                        );
                      })()}
                      <div style={{ fontSize: 13, marginTop: 12, color: gold, fontWeight: 700, letterSpacing: 0.5 }}>₹{s.hourly_rate}/hr <span style={{ opacity: 0.5, margin: "0 4px" }}>·</span> ½d ₹{s.halfday_rate}</div>
                    </button>
                  ))}
                  {spacesForStudio.length === 0 && <div style={{ fontSize: 13, color: "#666", fontStyle: "italic", gridColumn: "1/-1" }}>No spaces configured for this studio yet.</div>}
                </div>
                {showPackages && cfg.packages?.length > 0 && (
                  <div style={{ marginTop: 24 }}>
                    <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 2, color: "#666", fontWeight: 700, marginBottom: 8 }}>Or pick a preset package</div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 12 }}>
                      {cfg.packages.map((p) => (
                        <button key={p.id} type="button" onClick={() => { setPackageId(p.id); goStep(2); }}
                                style={{ padding: 16, border: `2px solid ${packageId === p.id ? primary : "#e5e5e5"}`, background: "white", textAlign: "left", cursor: "pointer" }}>
                          <div style={{ fontWeight: 700 }}>{p.name}</div>
                          <div style={{ fontSize: 11 }}>{p.description}</div>
                          <div style={{ fontSize: 12, color: primary, marginTop: 8 }}>₹{p.price} · {p.duration_hours} hrs</div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}

            {isOffsite && layoutMode !== "compact" && (
              <div className="lx-continue-row" style={{ display: "flex", justifyContent: "center", marginTop: 24 }}>
                <button onClick={() => goStep(2)} className="lx-btn-primary"
                          style={{ padding: "14px 32px", background: bark800, color: "white", border: 0, cursor: "pointer", width: "100%", maxWidth: 360 }}>Continue →</button>
              </div>
            )}
          </div>
        )}

        {(step === 2 || showAllSteps) && (
          <div data-testid="widget-step-2">
            {/* Feb 2026 · R268kkk · Two-column layout — calendar on
                left (constrained width so it doesn't stretch across
                the whole card), time selectors + shoot details on
                right. Stacks on mobile (<768px). Fixes the "calendar
                is huge and left-aligned with cramped time inputs
                below" issue reported after the earlier polish pass. */}
            {showAllSteps && (
              <div style={{ margin: "12px 0 20px", padding: "12px 14px", background: cream100, borderLeft: `3px solid ${gold}`,
                            fontSize: 10, textTransform: "uppercase", letterSpacing: 3, color: bark600, fontWeight: 700 }}>
                Date & time · Section 2 of 4
              </div>
            )}
            {!isOffsite && currentStudio && !showAllSteps && (
              <CallWhatsAppBar studio={currentStudio} primary={primary}
                                cream100={cream100} cream200={cream200} />
            )}
            <div style={{
              display: "grid",
              gridTemplateColumns: "minmax(260px, 340px) 1fr",
              gap: 28,
            }} className="lx-step2-grid">
              {/* LEFT · Calendar */}
              <div>
                <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 2, color: gold, fontWeight: 700, marginBottom: 4 }}>Section 1 of 2</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: bark800, marginBottom: 14, letterSpacing: -0.3 }}>Date &amp; time</div>
                <div className="lx-calendar-wrap" style={{ marginBottom: 16 }}>
                  <div className="lx-calendar-inner" style={{
                    background: "white",
                    border: `1px solid ${cream200}`,
                    padding: 12,
                    display: "flex",
                    justifyContent: "center",
                  }}>
                    <ShadCalendar
                      mode="single"
                      selected={date ? new Date(date + "T00:00:00") : undefined}
                      onSelect={(d) => { if (d) setDate(toLocalYMD(d)); }}
                      disabled={{ before: new Date(new Date().toDateString()) }}
                      modifiers={{ picked: date ? [new Date(date + "T00:00:00")] : [] }}
                      modifiersStyles={{ picked: { background: gold, color: "white", borderRadius: 4 } }}
                      data-testid="widget-calendar"
                    />
                  </div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, maxWidth: 420, margin: "0 auto", justifyItems: "center" }}>
                  <div style={{ width: "100%", textAlign: "center" }}>
                    <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 2, color: bark600, fontWeight: 700, marginBottom: 6 }}>Start time</div>
                    <TimeSelect value={startTime}
                                 onChange={(v) => {
                                   setStartTime(v);
                                   // Feb 2026 · R268lll · Smart end-time —
                                   // if start moves past end (or is <1h before), auto-advance
                                   // end to start + 1h. Uses 24h HH:MM math.
                                   const [sh, sm] = v.split(":").map(Number);
                                   const [eh, em] = (endTime || "00:00").split(":").map(Number);
                                   const startMin = sh * 60 + sm;
                                   const endMin = eh * 60 + em;
                                   if (endMin <= startMin || endMin < startMin + 60) {
                                     const targetMin = Math.min(startMin + 60, 23 * 60 + 59);
                                     const th = String(Math.floor(targetMin / 60)).padStart(2, "0");
                                     const tm = String(targetMin % 60).padStart(2, "0");
                                     setEndTime(`${th}:${tm}`);
                                   }
                                 }}
                                 accent={accent} primary={primary} testid="widget-start" />
                  </div>
                  <div style={{ width: "100%", textAlign: "center" }}>
                    <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 2, color: bark600, fontWeight: 700, marginBottom: 6 }}>End time</div>
                    <TimeSelect value={endTime} onChange={setEndTime} accent={accent} primary={primary} testid="widget-end" />
                  </div>
                </div>
                {/* Oct 2026 · Slot availability + selected-slot summary
                     moved directly under the Start/End pickers so the
                     customer sees whether their chosen slot is bookable
                     without hopping across columns. Only shows the
                     studio-level status (space & hours); equipment/crew
                     unavailability is surfaced inline on each tile. */}
                {studioBlocked && (
                  <div data-testid="widget-avail-blocked"
                       style={{ padding: 12, background: "#fff2f2", border: "1px solid #f5b5b5", color: "#8a1f1f", fontSize: 13, marginTop: 16 }}>
                    <div style={{ fontWeight: 700, marginBottom: 4 }}>⚠ Selected slot is not available</div>
                    {(avail.conflicts?.off_hours || []).length > 0 && (
                      <div style={{ fontSize: 12 }}>· Please pick another time or date.</div>
                    )}
                    {(avail.conflicts?.space || []).length > 0 && (
                      (() => {
                        const shared = (avail.conflicts.space || []).find((c) => c.reason === "studio_shared_block");
                        return shared
                          ? <div style={{ fontSize: 12 }}>
                              · The whole studio is booked during this slot
                              {shared.sibling_space_name && ` (${shared.sibling_space_name} is a shared-facility space, so the entire studio is locked)`}
                            </div>
                          : <div style={{ fontSize: 12 }}>· This space is already booked in this slot</div>;
                      })()
                    )}
                    {/* Feb 2026 · Fallback nudge only when the specific conflict
                        bullet above hasn't already said "pick another time".
                        Avoids showing two near-identical lines. */}
                    {!(avail.conflicts?.off_hours || []).length
                      && !(avail.conflicts?.space || []).length && (
                      <div style={{ fontSize: 11, color: "#a05050", marginTop: 6 }}>Pick another date or time to continue.</div>
                    )}
                  </div>
                )}
                {avail && !studioBlocked && date && (!isOffsite ? spaceId : true) && (
                  <div data-testid="widget-avail-ok"
                       style={{ padding: 10, background: "#eefbf3", border: "1px solid #b7e5c9", color: "#1a6b39", fontSize: 12, marginTop: 16, textAlign: "center", fontWeight: 600 }}>
                    ✓ Slot is available
                  </div>
                )}
                {date && (
                  <div style={{ marginTop: 16 }}>
                    <SlotSummaryStrip
                      date={date} startTime={startTime} endTime={endTime}
                      spaceLabel={currentSpace?.name} studioLabel={currentStudio?.name}
                      isOffsite={isOffsite} editable={false}
                      amenities={currentAmenities}
                      cream100={cream100} cream200={cream200} gold={gold}
                      bark600={bark600} bark800={bark800} />
                  </div>
                )}
              </div>
              {/* RIGHT · Shoot details + availability + summary */}
              <div>
                <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 2, color: gold, fontWeight: 700, marginBottom: 4 }}>Section 2 of 2</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: bark800, marginBottom: 14, letterSpacing: -0.3 }}>Shoot details</div>
                {isOffsite && (
                  <div style={{ marginBottom: 14 }}>
                    <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 2, color: bark600, fontWeight: 700, marginBottom: 6 }}>Shoot location *</div>
                    <textarea placeholder="Full address of the shoot location"
                               value={locationNotes}
                               onChange={(e) => { setLocationNotes(e.target.value); clearInvalid("location"); }}
                               className={`lx-input ${invalidFields.has("location") ? "lx-invalid" : ""}`}
                               style={{ width: "100%", padding: 12, border: `1px solid ${locationNotes.trim() ? cream200 : "#e5a5a5"}`, background: "white", fontSize: 14, resize: "vertical", boxSizing: "border-box", outline: outlineIf("location") }}
                               rows={2} data-testid="widget-location" required />
                  </div>
                )}
                {/* Oct 2026 · Availability block + slot summary moved
                     to under the Start/End pickers in the left column
                     so the customer sees whether their slot is
                     bookable right where they chose it. */}
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 2, color: bark600, fontWeight: 700, marginBottom: 6 }}>Shoot brief *</div>
                  <textarea placeholder="What are you filming? Include references, mood and deliverables."
                             value={shootDetails}
                             onChange={(e) => setShootDetails(e.target.value)}
                             className="lx-input"
                             style={{ width: "100%", padding: 12, border: `1px solid ${shootDetails.trim() ? cream200 : "#e5a5a5"}`, background: "white", fontSize: 14, resize: "vertical", boxSizing: "border-box" }}
                             rows={5} data-testid="widget-shoot-details" required />
                </div>
              </div>
            </div>
            {layoutMode !== "compact" && (
            <div className="lx-continue-row" style={{ display: "flex", justifyContent: "center", marginTop: 24 }}>
              <button disabled={!date || studioBlocked}
                      onClick={() => goStep(3)}
                      className="lx-btn-primary"
                      style={{ padding: "14px 32px",
                                background: (date && !studioBlocked) ? bark800 : "#D4CBB8",
                                color: "white", border: 0,
                                cursor: (date && !studioBlocked) ? "pointer" : "not-allowed",
                                width: "100%", maxWidth: 360 }}
                      data-testid="widget-next-2">Continue →</button>
            </div>
            )}
          </div>
        )}

        {(step === 3 || showAllSteps) && (
          <div data-testid="widget-step-3">
            {showAllSteps && (
              <div style={{ margin: "20px 0", padding: "12px 14px", background: cream100, borderLeft: `3px solid ${gold}`,
                            fontSize: 10, textTransform: "uppercase", letterSpacing: 3, color: bark600, fontWeight: 700 }}>
                Extras & services · Section 3 of 4
              </div>
            )}
            {!isOffsite && currentStudio && !showAllSteps && (
              <CallWhatsAppBar studio={currentStudio} primary={primary}
                                cream100={cream100} cream200={cream200} />
            )}
            {/* Feb 2026 · Persistent slot summary — customer sees the
                exact date+time they're paying for while picking add-ons. */}
            {!showAllSteps && (
            <SlotSummaryStrip
              date={date} startTime={startTime} endTime={endTime}
              spaceLabel={currentSpace?.name} studioLabel={currentStudio?.name}
              isOffsite={isOffsite}
              amenities={currentAmenities}
              onEdit={() => goStep(2)}
              cream100={cream100} cream200={cream200} gold={gold}
              bark600={bark600} bark800={bark800} />
            )}{showEquipment && cfg.equipment?.length > 0 && (<>
              <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 2, fontWeight: 700, marginBottom: 8 }}>Equipment (optional)</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 8, marginBottom: 20 }}>
                {cfg.equipment.map((e) => {
                  const picked = equipmentIds.includes(e.id);
                  // Oct 2026 · Per-item availability — unavailable
                  // items show a small red note inside the tile and
                  // the checkbox is disabled. The customer can still
                  // book the studio with the remaining equipment.
                  const blocked = unavailableEquipmentIds.has(e.id);
                  return (
                    <label key={e.id}
                           style={{
                             padding: 8,
                             border: `1px solid ${blocked ? "#f3c7c7" : (picked ? primary : "#e5e5e5")}`,
                             background: blocked ? "#fafafa" : "white",
                             cursor: blocked ? "not-allowed" : "pointer",
                             display: "block",
                             opacity: blocked ? 0.75 : 1,
                             position: "relative",
                           }}
                           data-testid={`widget-equipment-${e.id}`}
                           data-blocked={blocked ? "1" : "0"}>
                      <input type="checkbox" checked={picked && !blocked} disabled={blocked}
                             onChange={() => {
                               if (blocked) return;
                               setEquipmentIds((s) => picked ? s.filter((x) => x !== e.id) : [...s, e.id]);
                             }}
                             data-testid={`widget-equip-${e.id}`} />
                      <span style={{ marginLeft: 4, color: blocked ? bark600 : undefined, textDecoration: blocked ? "line-through" : "none" }}>
                        {e.name}
                      </span>
                      <div style={{ fontSize: 11, color: "#666" }}>₹{e.hourly_rate}/hr</div>
                      {blocked && (
                        <div style={{ marginTop: 4, fontSize: 10, color: "#b91c1c", fontWeight: 700, letterSpacing: 0.3 }}
                             data-testid={`widget-equipment-unavail-${e.id}`}>
                          Not available for selected slot
                        </div>
                      )}
                    </label>
                  );
                })}
              </div>
            </>)}
            {showCrew && cfg.crew_roles?.length > 0 && (<>
              <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 2, fontWeight: 700, marginBottom: 8 }}>Crew (optional)</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 8, marginBottom: 20 }}>
                {cfg.crew_roles.map((c) => {
                  const item = crewRoles.find((x) => x.role_id === c.id);
                  return (
                    <label key={c.id} style={{ padding: 8, border: `1px solid ${item ? primary : "#e5e5e5"}`, display: "block" }}>
                      <input type="checkbox" checked={!!item} onChange={() => setCrewRoles((s) => item ? s.filter((x) => x.role_id !== c.id) : [...s, { role_id: c.id, count: 1 }])} data-testid={`widget-crew-${c.id}`} /> {c.name}
                      <div style={{ fontSize: 11, color: "#666" }}>₹{c.hourly_rate}/hr</div>
                    </label>
                  );
                })}
              </div>
            </>)}
            {servicesCatalog.length > 0 && (<>
              <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 2, fontWeight: 700, marginBottom: 6, color: bark800 }}>Additional services (optional)</div>
              {wc.additional_services_html ? (
                <div className="lx-services-html" style={{ fontSize: 13, color: bark800, marginBottom: 10, padding: "10px 12px", background: cream100, border: `1px solid ${cream200}`, borderLeft: `3px solid ${gold}`, lineHeight: 1.55 }}
                     dangerouslySetInnerHTML={{ __html: wc.additional_services_html }} />
              ) : (
                <div style={{ fontSize: 12, color: bark600, marginBottom: 10, padding: "8px 12px", background: cream100, border: `1px solid ${cream200}`, borderLeft: `3px solid ${gold}` }}>
                  ℹ Studio, equipment & crew above are booked instantly. Anything you pick below is a special request — <b>we'll send a separate quotation over email</b> after the booking is confirmed.
                </div>
              )}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 8, marginBottom: 24 }}
                   data-testid="widget-services-grid">
                {servicesCatalog.map((s) => {
                  const picked = additionalServices.includes(s.name);
                  return (
                    <label key={s.id} style={{ padding: 10, border: `1px solid ${picked ? gold : cream200}`, borderLeft: `3px solid ${picked ? gold : cream200}`, cursor: "pointer", display: "block", background: picked ? cream100 : "white" }}
                             data-testid={`widget-service-${s.id}`}>
                      <input type="checkbox" checked={picked}
                              onChange={() => setAdditionalServices((prev) => picked ? prev.filter((x) => x !== s.name) : [...prev, s.name])} />
                      <span style={{ marginLeft: 6, fontWeight: 600, color: bark800 }}>{s.name}</span>
                      {s.description && <div style={{ fontSize: 11, color: bark600, marginTop: 2, marginLeft: 22 }}>{s.description}</div>}
                    </label>
                  );
                })}
              </div>
            </>)}
            {quote && !showAllSteps && <QuoteSummary q={quote} primary={primary} accent={accent} />}
            {layoutMode !== "compact" && (
            <div className="lx-continue-row" style={{ display: "flex", justifyContent: "center", marginTop: 24 }}>
              <button onClick={() => goStep(4)} className="lx-btn-primary"
                       style={{ padding: "14px 32px", background: bark800, color: "white", border: 0, cursor: "pointer", width: "100%", maxWidth: 360 }}
                       data-testid="widget-next-3">Continue →</button>
            </div>
            )}
          </div>
        )}

        {(step === 4 || showAllSteps) && (
          <div data-testid="widget-step-4">
            {showAllSteps && (
              <div style={{ margin: "20px 0", padding: "12px 14px", background: cream100, borderLeft: `3px solid ${gold}`,
                            fontSize: 10, textTransform: "uppercase", letterSpacing: 3, color: bark600, fontWeight: 700 }}>
                Your details & payment · Section 4 of 4
              </div>
            )}
            {!isOffsite && currentStudio && !showAllSteps && (
              <CallWhatsAppBar studio={currentStudio} primary={primary}
                                cream100={cream100} cream200={cream200} />
            )}
            {/* Feb 2026 · Locked-in slot summary at the top of checkout —
                customer sees the exact date+time+space they're paying
                for on the same screen as the Book button. */}
            {!showAllSteps && (
            <SlotSummaryStrip
              date={date} startTime={startTime} endTime={endTime}
              spaceLabel={currentSpace?.name} studioLabel={currentStudio?.name}
              isOffsite={isOffsite}
              amenities={currentAmenities}
              onEdit={() => goStep(2)}
              cream100={cream100} cream200={cream200} gold={gold}
              bark600={bark600} bark800={bark800} />
            )}
            {quote && <QuoteSummary q={quote} primary={primary} accent={accent} />}
            {/* Feb 2026 · Promo / discount code */}
            <div style={{ marginTop: 16, padding: 16, background: cream100,
                              border: `1px solid ${promoStatus === "valid" ? "#059669" : promoStatus === "invalid" ? "#dc2626" : cream200}`,
                              borderLeft: `3px solid ${promoStatus === "valid" ? "#059669" : promoStatus === "invalid" ? "#dc2626" : gold}` }}
                 data-testid="widget-promo-block">
              <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 2.5, color: bark600, fontWeight: 700, marginBottom: 8 }}>
                Have a promo code?
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <input placeholder="Enter code (e.g. LAUNCH20)"
                       value={promoCode}
                       onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                       className="lx-input"
                       style={{ flex: 1, padding: 12, fontFamily: "monospace", textTransform: "uppercase",
                                    border: `1px solid ${cream200}`, background: "white", fontSize: 14, letterSpacing: 2, minWidth: 0 }}
                       data-testid="widget-promo-input" />
                {promoCode && (
                  <button type="button"
                          onClick={() => { setPromoCode(""); setPromoStatus(null); setPromoMsg(""); }}
                          style={{ padding: "0 16px", background: "transparent", border: `1px solid ${cream200}`, cursor: "pointer", fontSize: 11, color: bark600, textTransform: "uppercase", letterSpacing: 1.5, fontWeight: 700 }}
                          data-testid="widget-promo-clear">Clear</button>
                )}
              </div>
              {promoMsg && (
                <div style={{ marginTop: 8, fontSize: 12, color: promoStatus === "valid" ? "#059669" : "#dc2626", fontWeight: 600 }}
                     data-testid="widget-promo-msg">
                  {promoStatus === "valid" ? "✓ " : "⚠ "}{promoMsg}
                </div>
              )}
            </div>
            {/* Feb 2026 · R268yyy · autoComplete + name attrs so
                browsers offer the customer their saved Full-Name /
                Email / Phone (also nudges Chrome/Safari to save the
                fresh entries so the NEXT booking auto-fills). */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12, marginTop: 12 }}>
              <input placeholder="Full name*" value={name} onChange={(e) => { setName(e.target.value); clearInvalid("name"); }}
                     name="name" autoComplete="name" autoCapitalize="words" inputMode="text"
                     className={`lx-input ${invalidFields.has("name") ? "lx-invalid" : ""}`}
                     style={{ padding: 12, border: `1px solid ${cream200}`, background: "white", fontSize: 15, outline: outlineIf("name") }}
                     data-testid="widget-name" />
              <input placeholder="Email*" type="email" value={email} onChange={(e) => { setEmail(e.target.value); clearInvalid("email"); }}
                     name="email" autoComplete="email" inputMode="email" autoCapitalize="off" spellCheck="false"
                     className={`lx-input ${invalidFields.has("email") ? "lx-invalid" : ""}`}
                     style={{ padding: 12, border: `1px solid ${cream200}`, background: "white", fontSize: 15, outline: outlineIf("email") }}
                     data-testid="widget-email" />
              <input placeholder={reqPhone ? "Phone*" : "Phone"} value={phone} onChange={(e) => { setPhone(e.target.value); clearInvalid("phone"); }}
                     name="tel" autoComplete="tel" type="tel" inputMode="tel"
                     className={`lx-input ${invalidFields.has("phone") ? "lx-invalid" : ""}`}
                     style={{ padding: 12, border: `1px solid ${cream200}`, background: "white", fontSize: 15, outline: outlineIf("phone") }}
                     data-testid="widget-phone" />
              {(reqCompany || reqGstin) && (
                <input placeholder={reqCompany ? "Company*" : "Company"} value={company} onChange={(e) => { setCompany(e.target.value); clearInvalid("company"); }}
                       name="organization" autoComplete="organization"
                       className={`lx-input ${invalidFields.has("company") ? "lx-invalid" : ""}`}
                       style={{ padding: 12, border: `1px solid ${cream200}`, background: "white", fontSize: 15, outline: outlineIf("company") }}
                       data-testid="widget-company" />
              )}
              {reqGstin && (
                <input placeholder="GSTIN*" value={gstin} onChange={(e) => { setGstin(e.target.value.toUpperCase()); clearInvalid("gstin"); }}
                       className={`lx-input ${invalidFields.has("gstin") ? "lx-invalid" : ""}`}
                       style={{ padding: 12, border: `1px solid ${cream200}`, background: "white", fontSize: 15, outline: outlineIf("gstin") }}
                       data-testid="widget-gstin" />
              )}
            </div>
            {/* Feb 2026 · R268kkk · Shoot brief mirrored on Step 4 so
                the customer can fill it right at checkout — used to
                get stuck if they missed the field on Step 2. Same
                state as Step 2 so edits sync. Required. */}
            <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 2, color: bark600, fontWeight: 700, marginBottom: 6 }}>Shoot brief *</div>
              <textarea placeholder="What are you filming? Include references, mood and deliverables."
                         value={shootDetails}
                         onChange={(e) => { setShootDetails(e.target.value); clearInvalid("shootDetails"); }}
                         className={`lx-input ${invalidFields.has("shootDetails") ? "lx-invalid" : ""}`}
                         style={{ width: "100%", padding: 12, border: `1px solid ${shootDetails.trim() ? cream200 : "#e5a5a5"}`, background: "white", fontSize: 14, resize: "vertical", boxSizing: "border-box", outline: outlineIf("shootDetails") }}
                         rows={3} data-testid="widget-shoot-details-step4" required />
            </div>
            <textarea placeholder="Anything else we should know? (optional)" value={notes} onChange={(e) => setNotes(e.target.value)}
                       className="lx-input"
                       style={{ width: "100%", padding: 12, marginTop: 12, border: `1px solid ${cream200}`, background: "white", fontSize: 14, resize: "vertical" }}
                       rows={2} data-testid="widget-notes" />
            {wc.terms_html && <div style={{ marginTop: 12, fontSize: 11, color: bark600, lineHeight: 1.5 }} dangerouslySetInnerHTML={{ __html: wc.terms_html }} />}
            <div className="lx-continue-row" style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6, marginTop: 24 }}>
              <button onClick={book} disabled={busy || !canBook()}
                      className="lx-btn-primary"
                      style={{ marginTop: 4, padding: "16px 32px", background: canBook() ? bark800 : "#D4CBB8",
                                  color: "white", border: 0, cursor: canBook() ? "pointer" : "not-allowed",
                                  fontSize: 13, fontWeight: 700, width: "100%", maxWidth: 480, boxShadow: canBook() ? "0 12px 24px -8px rgba(36,26,17,0.35)" : "none" }}
                      data-testid="widget-pay">
                {busy ? "Processing…" : `${wc.cta_label || "Book now"}  ·  Pay ₹${quote?.deposit_due?.toFixed(2) || "0.00"} →`}
              </button>
              {/* R288 · Breakdown of what the "Pay ₹X" button is actually
                  charging — splits the deposit into the booking portion
                  and the (refundable) damage deposit so first-time
                  customers know the deposit comes back. */}
              {quote?.damage_deposit_upfront > 0 && (
                <div data-testid="widget-pay-breakdown"
                     style={{ fontSize: 11, color: bark600, textAlign: "center", lineHeight: 1.5, maxWidth: 480 }}>
                  <span style={{ fontVariantNumeric: "tabular-nums" }}>
                    ₹{(quote.deposit_due - quote.damage_deposit_upfront).toFixed(2)}
                  </span>{" "}shoot
                  {" · "}
                  <span style={{ fontVariantNumeric: "tabular-nums", color: gold, fontWeight: 700 }}>
                    ₹{quote.damage_deposit_upfront.toFixed(2)}
                  </span>{" "}refundable deposit (returned after your shoot)
                </div>
              )}
            </div>
            {/* R289 · Oct 2026 · Pre-book walk-in payment option. When
                the studio has configured a UPI QR / UPI ID, show a
                collapsible "Walk-in? Scan to pay the deposit now"
                block directly below the Pay button so customers who
                are already at the studio can lock their slot via a
                single QR scan — the booking still goes through the
                normal (pending_verification) flow, but the deposit
                is already in-hand. Hidden when the studio uses
                Cashfree-only mode. */}
            {quote?.deposit_due > 0 && (currentStudio?.upi_qr_url || currentStudio?.upi_id) && (
              <PreBookWalkinPanel
                studio={currentStudio}
                amount={quote.deposit_due}
                cream100={cream100} cream200={cream200} gold={gold}
                bark600={bark600} bark800={bark800} primary={primary}
                serifFamily={serifFamily} font={font}
              />
            )}
          </div>
        )}
        {/* Feb 2026 · R268ppp · Powered-by hyperlink footer — an
            unobtrusive discovery hook for prospective Acquilix users
            (studio owners visiting each other's booking pages). Can be
            hidden via `wc.show_powered_by === false`. */}
        {wc.show_powered_by !== false && (
          <div data-testid="powered-by"
                style={{
                  textAlign: "center", marginTop: 24, fontSize: 10,
                  letterSpacing: 3, textTransform: "uppercase",
                  color: bark600, opacity: 0.75,
                }}>
            Booking experience powered by{" "}
            <a href="https://acquilix.com" target="_blank" rel="noopener noreferrer"
                style={{ color: "inherit", textDecoration: "none",
                          letterSpacing: 3, fontWeight: 700 }}>
              Acquilix<sup style={{ fontSize: "0.6em", fontWeight: 400, marginLeft: 1 }}>™</sup>
            </a>
          </div>
        )}
        </div>{/* /card */}
      </div>
      {/* R268yyy · Feb 2026 · Sticky floating pay bar for compact
           single-page mode. Follows the customer down the page so they
           never need to scroll back to submit. Shows the running total
           on the left (with a subtle pulse each time the quote updates
           so tweaks in extras/crew feel responsive) and the primary
           pay CTA on the right. Tuned to stay side-by-side even on
           <400px viewports via the .lx-sticky-pay-* CSS above. */}
      {showAllSteps && !done && (
        <div className="lx-sticky-pay" data-testid="widget-sticky-pay" style={{
          position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 40,
          background: "rgba(255,255,255,0.96)", backdropFilter: "blur(10px)",
          borderTop: `1px solid ${cream200}`,
          boxShadow: "0 -8px 24px -12px rgba(36,26,17,0.25)",
          padding: "12px 20px",
        }}>
          <div className="lx-sticky-pay-inner">
            <div className={`lx-sticky-pay-info${pulseTotal ? " lx-pulse" : ""}`}>
              <span className="lx-sticky-total-label"
                     style={{ fontSize: 9, textTransform: "uppercase", letterSpacing: 2.5, color: bark600, fontWeight: 700 }}>
                {quote ? `Total ₹${quote.total?.toFixed(2) || "0.00"}` : "Pick a space & date to see quote"}
              </span>
              {quote?.deposit_due > 0 && (
                <span className="lx-sticky-deposit"
                        style={{ fontSize: 11, color: bark800, fontWeight: 700 }}>
                  {quote?.damage_deposit_upfront > 0 ? (
                    <>
                      Pay now: <span style={{ fontVariantNumeric: "tabular-nums" }}>
                        ₹{(quote.deposit_due - quote.damage_deposit_upfront).toFixed(2)}
                      </span>
                      {" "}+ <span style={{ color: gold, fontVariantNumeric: "tabular-nums" }}>
                        ₹{quote.damage_deposit_upfront.toFixed(2)} deposit
                      </span>
                    </>
                  ) : (
                    <>Deposit due ₹{quote.deposit_due.toFixed(2)}</>
                  )}
                </span>
              )}
            </div>
            <button className="lx-sticky-pay-btn"
                      onClick={book} disabled={busy || !canBook()}
                      style={{ padding: "12px 22px",
                                background: canBook() ? bark800 : "#D4CBB8",
                                color: "white", border: 0,
                                cursor: canBook() ? "pointer" : "not-allowed",
                                fontSize: 12, fontWeight: 700, letterSpacing: 1.5,
                                textTransform: "uppercase" }}
                      data-testid="widget-sticky-pay-btn">
              {busy ? "Processing…" : `${wc.cta_label || "Book now"} · ₹${quote?.deposit_due?.toFixed(2) || "0.00"} →`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function QuoteSummary({ q, primary, accent }) {
  const cream100 = "#FBF7F0";
  const cream200 = "#F3ECE0";
  const bark800 = "#241A11";
  const bark600 = "#5C4A38";
  const gold = "#B8860B";
  const serifFamily = `"Cormorant Garamond", Georgia, serif`;
  return (
    <div style={{ padding: 20, background: cream100, marginBottom: 16, border: `1px solid ${cream200}`, borderLeft: `3px solid ${gold}` }} data-testid="widget-quote">
      <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 3, color: bark600, marginBottom: 12, fontWeight: 700 }}>Your Quote</div>
      {q.items.map((it, i) => (
        <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "3px 0", color: bark800 }}>
          <span>{it.name}{it.qty > 1 && ` × ${it.qty}${it.unit || ""}`}</span>
          <span style={{ fontVariantNumeric: "tabular-nums" }}>₹{it.amount?.toFixed(2)}</span>
        </div>
      ))}
      <hr style={{ margin: "10px 0", border: 0, borderTop: `1px solid ${cream200}` }} />
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: bark600 }}><span>Subtotal</span><span style={{ fontVariantNumeric: "tabular-nums" }}>₹{q.subtotal?.toFixed(2)}</span></div>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: bark600 }}><span>GST @ {q.tax_pct}%</span><span style={{ fontVariantNumeric: "tabular-nums" }}>₹{q.tax?.toFixed(2)}</span></div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", fontSize: 22, fontWeight: 500, marginTop: 8, fontFamily: serifFamily, color: bark800 }}>
        <span style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 2, color: bark800, fontWeight: 700, fontFamily: "inherit" }}>Total</span>
        <span style={{ fontVariantNumeric: "tabular-nums" }}>₹{q.total?.toFixed(2)}</span>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginTop: 8, color: gold, fontWeight: 700 }}><span>Deposit due now ({q.deposit_pct}%)</span><span style={{ fontVariantNumeric: "tabular-nums" }}>₹{q.deposit_due?.toFixed(2)}</span></div>
      {/* R288 · Split refundable callout: when upfront, the deposit_due
          line already includes the damage deposit so we note it as a
          sub-item. When collected on-site, we show it as an informational
          "+" line beneath (same as the pre-R288 behaviour). */}
      {q.damage_deposit > 0 && q.damage_deposit_upfront > 0 && (
        <div style={{ fontSize: 11, color: bark600, marginTop: 4, paddingLeft: 10, borderLeft: `2px solid ${cream200}` }}>
          includes <b>₹{q.damage_deposit_upfront.toFixed(2)}</b> refundable damage deposit (returned after your shoot)
        </div>
      )}
      {q.damage_deposit > 0 && !q.damage_deposit_upfront && (
        <div style={{ fontSize: 11, color: bark600, marginTop: 4 }}>
          + ₹{q.damage_deposit} refundable damage deposit (collected on-site, returned after your shoot)
        </div>
      )}
    </div>
  );
}

// Feb 2026 · Branded time picker — 30-min increments from 06:00 → 22:30.
function TimeSelect({ value, onChange, primary, accent, testid }) {
  const options = [];
  for (let h = 6; h <= 22; h++) {
    for (const m of [0, 30]) {
      const v = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
      options.push(v);
    }
  }
  options.push("23:00");
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)}
            data-testid={testid}
            style={{ padding: "8px 10px", fontSize: 14, border: `1px solid #d4d4d4`,
                       background: "white", color: "#111",
                       borderRadius: 2, minWidth: 100, cursor: "pointer",
                       outlineColor: primary }}>
      {options.map((t) => <option key={t} value={t}>{t}</option>)}
    </select>
  );
}
